import {
  BadRequestException,
  ConflictException,
  Inject,
  Injectable,
  NotFoundException,
  Optional,
} from '@nestjs/common';
import { Prisma } from '@prisma/client';
import { randomUUID } from 'node:crypto';
import {
  IMAGE_STORAGE_ADAPTER,
  ImageStorageAdapter,
  PrismaService,
} from '@cart/api-data-access';
import { AuditService } from '../audit/audit.service';
import { AuditTransactionService } from '../audit/audit-transaction.service';
import { MediaUploadService } from '../media/media-upload.service';
import { EmailService } from '../notifications/email.service';
import { StoreContextService } from '../tenancy/store-context.service';
import { InventoryLedgerService } from '../inventory/inventory-ledger.service';
import { MediaAssetService } from '../media/media-asset.service';
import { compressImage, isCompressible } from './image-compression.util';
import { ImageQuotaService } from './image-quota.service';
import { purgeProductImage } from './image-purge.util';
import { ImageSettingsService, UpdateOrgImageSettingsInput } from './image-settings.service';

export type RawQuery = Record<string, string | undefined>;

@Injectable()
export class CommerceAdminService {
  constructor(
    private readonly prisma: PrismaService,
    private readonly auditService: AuditService,
    private readonly storeContext?: StoreContextService,
    private readonly imageQuota?: ImageQuotaService,
    @Inject(IMAGE_STORAGE_ADAPTER) private readonly storageAdapter?: ImageStorageAdapter,
    private readonly imageSettings?: ImageSettingsService,
    private readonly emailService?: EmailService,
    /**
     * Optional like the collaborators above so the existing specs can build
     * this service without the whole media stack. When Nest binds it (always,
     * in production), uploads route through the media registry instead of
     * writing straight to a single storage adapter.
     */
    private readonly mediaUpload?: MediaUploadService,
    /**
     * Optional for the same reason as the rest: the specs build this service
     * bare. Without it, variants get no ledger line and checkout skips them.
     */
    private readonly ledger?: InventoryLedgerService,
    /**
     * Optional like the rest so specs can build this service bare. Without it
     * a deleted image leaves its bytes counted forever — see
     * deleteProductImage().
     */
    private readonly mediaAssets?: MediaAssetService,
    /**
     * Optional like the collaborators above. Without it the trigger still
     * writes the change rows; they just carry `system` as the actor.
     */
    @Optional()
    private readonly auditTx?: AuditTransactionService
  ) {}

  /**
   * The image collaborators are constructor-optional so specs can build this
   * service without them; Nest always binds them in CommerceAdminModule. These
   * accessors replace a `!` at each use with a check that names what is missing.
   */
  private get images(): ImageSettingsService {
    if (!this.imageSettings) throw new Error('ImageSettingsService is not available on CommerceAdminService');
    return this.imageSettings;
  }

  private get quota(): ImageQuotaService {
    if (!this.imageQuota) throw new Error('ImageQuotaService is not available on CommerceAdminService');
    return this.imageQuota;
  }

  private get storage(): ImageStorageAdapter {
    if (!this.storageAdapter) throw new Error('IMAGE_STORAGE_ADAPTER is not bound on CommerceAdminService');
    return this.storageAdapter;
  }

  async listOrganizations(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.organization.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { name: 'asc' }],
        ...paging,
      }),
      this.prisma.organization.count({ where }),
    ]);

    return { items, total };
  }

  async createOrganization(body: Record<string, unknown>) {
    const name = this.requiredString(body, 'name');
    const slug = this.slug(body, name);
    await this.assertOrganizationSlugAvailable(slug);
    const item = await this.prisma.organization.create({
      data: {
        name,
        slug,
      },
    });

    await this.logChange('platform', 'organizations', item.id, 'create', item);
    return item;
  }

  async updateOrganization(
    organizationId: string,
    body: Record<string, unknown>
  ) {
    await this.assertOrganization(organizationId);
    const data: Prisma.OrganizationUpdateInput = {};
    const name = this.optionalString(body, 'name');
    const slug = this.optionalString(body, 'slug');

    if (name !== undefined) data.name = name;
    if (slug !== undefined || name !== undefined) {
      // The guard above guarantees at least one of name/slug is defined.
      const nextSlug = this.slug(body, (name ?? slug) as string);
      await this.assertOrganizationSlugAvailable(nextSlug, organizationId);
      data.slug = nextSlug;
    }
    this.assignOptionalNumber(data, body, 'sortOrder');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException(
        'at least one organization field is required'
      );
    }

    const item = await this.prisma.organization.update({
      where: { id: organizationId },
      data,
    });

    await this.logChange('platform', 'organizations', item.id, 'update', item);
    return item;
  }

  async deleteOrganization(organizationId: string) {
    await this.assertOrganization(organizationId);
    const item = await this.prisma.organization.update({
      where: { id: organizationId },
      data: { isActive: false },
    });

    await this.logChange('platform', 'organizations', item.id, 'delete', item);
    return item;
  }

  async listStores(query: RawQuery = {}) {
    const paging = this.paging(query);
    const organizationId = query.organizationId ?? this.organizationId();
    const where = { organizationId, isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.store.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { name: 'asc' }],
        ...paging,
      }),
      this.prisma.store.count({ where }),
    ]);

    return { items, total };
  }

  async createStore(body: Record<string, unknown>) {
    const organizationId =
      this.optionalString(body, 'organizationId') ?? this.organizationId();
    await this.assertOrganization(organizationId);
    const name = this.requiredString(body, 'name');
    const slug = this.slug(body, name);
    await this.assertStoreSlugAvailable(organizationId, slug);
    const item = await this.prisma.store.create({
      data: {
        organizationId,
        name,
        slug,
        status: this.optionalString(body, 'status') ?? 'active',
        defaultCurrency:
          this.optionalString(body, 'defaultCurrency')?.toUpperCase() ?? 'INR',
        defaultLocale: this.optionalString(body, 'defaultLocale') ?? 'en-US',
        timezone: this.optionalString(body, 'timezone') ?? 'UTC',
      },
    });

    await this.logChange('store', 'stores', item.id, 'create', item);
    return item;
  }

  async updateStore(storeId: string, body: Record<string, unknown>) {
    await this.assertStore(storeId);
    const data: Prisma.StoreUpdateInput = {};
    const name = this.optionalString(body, 'name');
    const slug = this.optionalString(body, 'slug');

    if (name !== undefined) data.name = name;
    if (slug !== undefined || name !== undefined) {
      // The guard above guarantees at least one of name/slug is defined.
      const nextSlug = this.slug(body, (name ?? slug) as string);
      await this.assertStoreSlugAvailable(this.organizationId(), nextSlug, storeId);
      data.slug = nextSlug;
    }
    this.assignOptionalString(data, body, 'status');
    this.assignOptionalString(data, body, 'defaultLocale');
    this.assignOptionalString(data, body, 'timezone');
    this.assignOptionalNumber(data, body, 'sortOrder');
    const defaultCurrency = this.optionalString(body, 'defaultCurrency');
    if (defaultCurrency !== undefined)
      data.defaultCurrency = defaultCurrency.toUpperCase();

    if (Object.keys(data).length === 0) {
      throw new BadRequestException('at least one store field is required');
    }

    const item = await this.prisma.store.update({
      where: { id: storeId },
      data,
    });

    await this.logChange('store', 'stores', item.id, 'update', item);
    return item;
  }

  async deleteStore(storeId: string) {
    await this.assertStore(storeId);
    const item = await this.prisma.store.update({
      where: { id: storeId },
      data: { isActive: false },
    });

    await this.logChange('store', 'stores', item.id, 'delete', item);
    return item;
  }

  async listStoreStaff(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.storeStaff.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { role: 'asc' }],
        ...paging,
      }),
      this.prisma.storeStaff.count({ where }),
    ]);

    return { items, total };
  }

  async addStoreStaff(body: Record<string, unknown>) {
    const userId = this.requiredString(body, 'userId');
    await this.assertUser(userId);
    const item = await this.prisma.storeStaff.create({
      data: {
        storeId: this.storeId(),
        userId,
        role: this.optionalString(body, 'role') ?? 'staff',
        permissions: this.optionalJson(body, 'permissions'),
      },
    });

    await this.logChange('store', 'store_staff', item.id, 'create', item);
    return item;
  }

  async updateStoreStaff(staffId: string, body: Record<string, unknown>) {
    await this.assertStoreStaff(staffId);
    const data: Prisma.StoreStaffUpdateInput = {};

    this.assignOptionalString(data, body, 'role');
    this.assignOptionalNumber(data, body, 'sortOrder');
    if (body.permissions !== undefined)
      data.permissions = this.optionalJson(body, 'permissions');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException('at least one staff field is required');
    }

    const item = await this.prisma.storeStaff.update({
      where: { id: staffId },
      data,
    });

    await this.logChange('store', 'store_staff', item.id, 'update', item);
    return item;
  }

  async removeStoreStaff(staffId: string) {
    await this.assertStoreStaff(staffId);
    const item = await this.prisma.storeStaff.update({
      where: { id: staffId },
      data: { isActive: false },
    });

    await this.logChange('store', 'store_staff', item.id, 'delete', item);
    return item;
  }

  async listCustomers(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = {
      storeId: this.storeId(),
      isActive: true,
      ...(query.email
        ? { email: { contains: query.email.toLowerCase() } }
        : {}),
    };
    const [items, total] = await Promise.all([
      this.prisma.customer.findMany({
        where,
        orderBy: [{ createdAt: 'desc' }],
        ...paging,
      }),
      this.prisma.customer.count({ where }),
    ]);

    return { items, total };
  }

  async createCustomer(body: Record<string, unknown>) {
    const customer = await this.prisma.customer.create({
      data: {
        storeId: this.storeId(),
        email: this.requiredString(body, 'email').toLowerCase(),
        phone: this.optionalString(body, 'phone'),
        firstName: this.optionalString(body, 'firstName'),
        lastName: this.optionalString(body, 'lastName'),
        acceptsMarketing:
          this.optionalBoolean(body, 'acceptsMarketing') ?? false,
      },
    });

    await this.logChange(
      'customer',
      'customers',
      customer.id,
      'create',
      customer
    );
    return customer;
  }

  async updateCustomer(customerId: string, body: Record<string, unknown>) {
    await this.assertCustomer(customerId);
    const data: Prisma.CustomerUpdateInput = {};
    const email = this.optionalString(body, 'email');

    if (email !== undefined) data.email = email.toLowerCase();
    this.assignOptionalString(data, body, 'phone');
    this.assignOptionalString(data, body, 'firstName');
    this.assignOptionalString(data, body, 'lastName');
    this.assignOptionalBoolean(data, body, 'acceptsMarketing');
    this.assignOptionalNumber(data, body, 'sortOrder');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException('at least one customer field is required');
    }

    const customer = await this.prisma.customer.update({
      where: { id: customerId },
      data,
    });

    await this.logChange(
      'customer',
      'customers',
      customer.id,
      'update',
      customer
    );
    return customer;
  }

  async deleteCustomer(customerId: string) {
    await this.assertCustomer(customerId);
    const customer = await this.prisma.customer.update({
      where: { id: customerId },
      data: { isActive: false },
    });

    await this.logChange(
      'customer',
      'customers',
      customer.id,
      'delete',
      customer
    );
    return customer;
  }

  async listCustomerAddresses(customerId: string, query: RawQuery = {}) {
    await this.assertCustomer(customerId);
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), customerId, isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.customerAddress.findMany({
        where,
        orderBy: [{ isDefault: 'desc' }, { createdAt: 'desc' }],
        ...paging,
      }),
      this.prisma.customerAddress.count({ where }),
    ]);

    return { items, total };
  }

  async createCustomerAddress(
    customerId: string,
    body: Record<string, unknown>
  ) {
    await this.assertCustomer(customerId);
    const address = await this.prisma.customerAddress.create({
      data: {
        storeId: this.storeId(),
        customerId,
        label: this.optionalString(body, 'label'),
        recipientName: this.requiredString(body, 'recipientName'),
        line1: this.requiredString(body, 'line1'),
        line2: this.optionalString(body, 'line2'),
        city: this.requiredString(body, 'city'),
        state: this.requiredString(body, 'state'),
        postalCode: this.requiredString(body, 'postalCode'),
        country: this.requiredString(body, 'country').toUpperCase(),
        phone: this.optionalString(body, 'phone'),
        isDefault: this.optionalBoolean(body, 'isDefault') ?? false,
      },
    });

    await this.logChange(
      'customer',
      'customer_addresses',
      address.id,
      'create',
      address
    );
    return address;
  }

  async updateCustomerAddress(
    addressId: string,
    body: Record<string, unknown>
  ) {
    await this.assertCustomerAddress(addressId);
    const data: Prisma.CustomerAddressUpdateInput = {};
    const country = this.optionalString(body, 'country');

    this.assignOptionalString(data, body, 'label');
    this.assignOptionalString(data, body, 'recipientName');
    this.assignOptionalString(data, body, 'line1');
    this.assignOptionalString(data, body, 'line2');
    this.assignOptionalString(data, body, 'city');
    this.assignOptionalString(data, body, 'state');
    this.assignOptionalString(data, body, 'postalCode');
    if (country !== undefined) data.country = country.toUpperCase();
    this.assignOptionalString(data, body, 'phone');
    this.assignOptionalBoolean(data, body, 'isDefault');
    this.assignOptionalNumber(data, body, 'sortOrder');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException(
        'at least one customer address field is required'
      );
    }

    const address = await this.prisma.customerAddress.update({
      where: { id: addressId },
      data,
    });

    await this.logChange(
      'customer',
      'customer_addresses',
      address.id,
      'update',
      address
    );
    return address;
  }

  async deleteCustomerAddress(addressId: string) {
    await this.assertCustomerAddress(addressId);
    const address = await this.prisma.customerAddress.update({
      where: { id: addressId },
      data: { isActive: false },
    });

    await this.logChange(
      'customer',
      'customer_addresses',
      address.id,
      'delete',
      address
    );
    return address;
  }

  async listCustomerTags(customerId: string, query: RawQuery = {}) {
    await this.assertCustomer(customerId);
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), customerId, isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.customerTag.findMany({
        where,
        orderBy: [{ tag: 'asc' }],
        ...paging,
      }),
      this.prisma.customerTag.count({ where }),
    ]);

    return { items, total };
  }

  async createCustomerTag(customerId: string, body: Record<string, unknown>) {
    await this.assertCustomer(customerId);
    const tag = await this.prisma.customerTag.create({
      data: {
        storeId: this.storeId(),
        customerId,
        tag: this.requiredString(body, 'tag').toLowerCase(),
      },
    });

    await this.logChange('customer', 'customer_tags', tag.id, 'create', tag);
    return tag;
  }

  async deleteCustomerTag(tagId: string) {
    await this.assertCustomerTag(tagId);
    const tag = await this.prisma.customerTag.update({
      where: { id: tagId },
      data: { isActive: false },
    });

    await this.logChange('customer', 'customer_tags', tag.id, 'delete', tag);
    return tag;
  }

  async listCustomerSessions(customerId: string, query: RawQuery = {}) {
    await this.assertCustomer(customerId);
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), customerId, isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.customerSession.findMany({
        where,
        orderBy: [{ createdAt: 'desc' }],
        ...paging,
      }),
      this.prisma.customerSession.count({ where }),
    ]);

    return { items, total };
  }

  async createCustomerSession(
    customerId: string,
    body: Record<string, unknown>
  ) {
    await this.assertCustomer(customerId);
    const session = await this.prisma.customerSession.create({
      data: {
        storeId: this.storeId(),
        customerId,
        refreshTokenHash: this.requiredString(body, 'refreshTokenHash'),
        expiresOn: this.requiredDate(body, 'expiresOn'),
      },
    });

    await this.logChange(
      'customer',
      'customer_sessions',
      session.id,
      'create',
      session
    );
    return session;
  }

  async revokeCustomerSession(sessionId: string) {
    await this.assertCustomerSession(sessionId);
    const session = await this.prisma.customerSession.update({
      where: { id: sessionId },
      data: { revokedOn: new Date(), isActive: false },
    });

    await this.logChange(
      'customer',
      'customer_sessions',
      session.id,
      'update',
      session
    );
    return session;
  }

  async listCustomerWishlist(customerId: string, query: RawQuery = {}) {
    await this.assertCustomer(customerId);
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), customerId, isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.customerWishlistItem.findMany({
        where,
        orderBy: [{ createdAt: 'desc' }],
        ...paging,
      }),
      this.prisma.customerWishlistItem.count({ where }),
    ]);

    return { items, total };
  }

  async createCustomerWishlistItem(
    customerId: string,
    body: Record<string, unknown>
  ) {
    await this.assertCustomer(customerId);
    const productId = this.requiredString(body, 'productId');
    const variantId = this.optionalString(body, 'variantId');
    await this.assertProduct(productId);
    if (variantId) {
      await this.assertProductVariant(productId, variantId);
    }

    const item = await this.prisma.customerWishlistItem.create({
      data: {
        storeId: this.storeId(),
        customerId,
        productId,
        variantId,
      },
    });

    await this.logChange('customer', 'wishlist_items', item.id, 'create', item);
    return item;
  }

  async deleteCustomerWishlistItem(wishlistItemId: string) {
    await this.assertCustomerWishlistItem(wishlistItemId);
    const item = await this.prisma.customerWishlistItem.update({
      where: { id: wishlistItemId },
      data: { isActive: false },
    });

    await this.logChange('customer', 'wishlist_items', item.id, 'delete', item);
    return item;
  }

  async listRoles(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.platformRole.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { name: 'asc' }],
        ...paging,
      }),
      this.prisma.platformRole.count({ where }),
    ]);

    return { items, total };
  }

  async createRole(body: Record<string, unknown>) {
    const item = await this.prisma.platformRole.create({
      data: {
        name: this.requiredString(body, 'name'),
        code: this.requiredString(body, 'code').toUpperCase(),
      },
    });

    await this.logChange('platform', 'roles', item.id, 'create', item);
    return item;
  }

  async listPermissions(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = {
      isActive: true,
      ...(query.module ? { module: query.module } : {}),
    };
    const [items, total] = await Promise.all([
      this.prisma.platformPermission.findMany({
        where,
        orderBy: [{ module: 'asc' }, { name: 'asc' }],
        ...paging,
      }),
      this.prisma.platformPermission.count({ where }),
    ]);

    return { items, total };
  }

  async createPermission(body: Record<string, unknown>) {
    const item = await this.prisma.platformPermission.create({
      data: {
        code: this.requiredString(body, 'code').toUpperCase(),
        name: this.requiredString(body, 'name'),
        module: this.requiredString(body, 'module'),
      },
    });

    await this.logChange('platform', 'permissions', item.id, 'create', item);
    return item;
  }

  async assignRolePermission(body: Record<string, unknown>) {
    const roleId = this.requiredString(body, 'roleId');
    const permissionId = this.requiredString(body, 'permissionId');
    await this.assertRole(roleId);
    await this.assertPermission(permissionId);

    const item = await this.prisma.platformRolePermission.create({
      data: {
        roleId,
        permissionId,
      },
    });

    await this.logChange(
      'platform',
      'role_permissions',
      item.id,
      'create',
      item
    );
    return item;
  }

  async removeRolePermission(rolePermissionId: string) {
    await this.assertRolePermission(rolePermissionId);
    const item = await this.prisma.platformRolePermission.update({
      where: { id: rolePermissionId },
      data: { isActive: false },
    });

    await this.logChange(
      'platform',
      'role_permissions',
      item.id,
      'delete',
      item
    );
    return item;
  }

  async listBrands(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.brand.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { name: 'asc' }],
        ...paging,
      }),
      this.prisma.brand.count({ where }),
    ]);

    return { items, total };
  }

  async createBrand(body: Record<string, unknown>) {
    const name = this.requiredString(body, 'name');
    const slug = this.slug(body, name);
    await this.assertBrandSlugAvailable(this.storeId(), slug);
    const brand = await this.prisma.brand.create({
      data: { storeId: this.storeId(), name, slug },
    });

    await this.logChange('products', 'brands', brand.id, 'create', brand);
    return brand;
  }

  async updateBrand(brandId: string, body: Record<string, unknown>) {
    await this.assertBrand(brandId);
    const data: Prisma.BrandUpdateInput = {};
    const name = this.optionalString(body, 'name');
    const slug = this.optionalString(body, 'slug');
    const sortOrder = this.optionalNumber(body, 'sortOrder');

    if (name !== undefined) data.name = name;
    if (slug !== undefined || name !== undefined) {
      // The guard above guarantees at least one of name/slug is defined.
      const nextSlug = this.slug(body, (name ?? slug) as string);
      await this.assertBrandSlugAvailable(this.storeId(), nextSlug, brandId);
      data.slug = nextSlug;
    }
    if (sortOrder !== undefined) data.sortOrder = sortOrder;

    if (Object.keys(data).length === 0) {
      throw new BadRequestException('at least one brand field is required');
    }

    const brand = await this.prisma.brand.update({
      where: { id: brandId },
      data,
    });

    await this.logChange('products', 'brands', brand.id, 'update', brand);
    return brand;
  }

  async deleteBrand(brandId: string) {
    await this.assertBrand(brandId);
    const brand = await this.prisma.brand.update({
      where: { id: brandId },
      data: { isActive: false },
    });

    await this.logChange('products', 'brands', brand.id, 'delete', brand);
    return brand;
  }

  async listCollections(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.collection.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { title: 'asc' }],
        ...paging,
      }),
      this.prisma.collection.count({ where }),
    ]);

    return { items, total };
  }

  async createCollection(body: Record<string, unknown>) {
    const title = this.requiredString(body, 'title');
    const slug = this.slug(body, title);
    await this.assertCollectionSlugAvailable(this.storeId(), slug);
    const collection = await this.prisma.collection.create({
      data: {
        storeId: this.storeId(),
        title,
        slug,
        description: this.optionalString(body, 'description'),
        isManual: this.optionalBoolean(body, 'isManual') ?? true,
      },
    });

    await this.logChange(
      'products',
      'collections',
      collection.id,
      'create',
      collection
    );
    return collection;
  }

  async updateCollection(collectionId: string, body: Record<string, unknown>) {
    await this.assertCollection(collectionId);
    const data: Prisma.CollectionUpdateInput = {};
    const title = this.optionalString(body, 'title');
    const slug = this.optionalString(body, 'slug');
    const description = this.optionalString(body, 'description');
    const isManual = this.optionalBoolean(body, 'isManual');
    const sortOrder = this.optionalNumber(body, 'sortOrder');

    if (title !== undefined) data.title = title;
    if (slug !== undefined || title !== undefined) {
      // The guard above guarantees at least one of title/slug is defined.
      const nextSlug = this.slug(body, (title ?? slug) as string);
      await this.assertCollectionSlugAvailable(this.storeId(), nextSlug, collectionId);
      data.slug = nextSlug;
    }
    if (description !== undefined) data.description = description;
    if (isManual !== undefined) data.isManual = isManual;
    if (sortOrder !== undefined) data.sortOrder = sortOrder;

    if (Object.keys(data).length === 0) {
      throw new BadRequestException(
        'at least one collection field is required'
      );
    }

    const collection = await this.prisma.collection.update({
      where: { id: collectionId },
      data,
    });

    await this.logChange(
      'products',
      'collections',
      collection.id,
      'update',
      collection
    );
    return collection;
  }

  async deleteCollection(collectionId: string) {
    await this.assertCollection(collectionId);
    const collection = await this.prisma.collection.update({
      where: { id: collectionId },
      data: { isActive: false },
    });

    await this.logChange(
      'products',
      'collections',
      collection.id,
      'delete',
      collection
    );
    return collection;
  }

  async addProductToCollection(
    collectionId: string,
    body: Record<string, unknown>
  ) {
    await this.assertCollection(collectionId);
    const productId = this.requiredString(body, 'productId');
    await this.assertProduct(productId);

    const item = await this.prisma.collectionProduct.create({
      data: { storeId: this.storeId(), collectionId, productId },
    });

    await this.logChange(
      'products',
      'collection_products',
      item.id,
      'create',
      item
    );
    return item;
  }

  async listProductOptions(productId: string, query: RawQuery = {}) {
    await this.assertProduct(productId);
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), productId, isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.productOption.findMany({
        where,
        orderBy: [{ position: 'asc' }, { name: 'asc' }],
        ...paging,
      }),
      this.prisma.productOption.count({ where }),
    ]);

    return { items, total };
  }

  async createProductOption(productId: string, body: Record<string, unknown>) {
    await this.assertProduct(productId);
    const option = await this.prisma.productOption.create({
      data: {
        storeId: this.storeId(),
        productId,
        name: this.requiredString(body, 'name'),
        position: this.optionalNumber(body, 'position') ?? 0,
      },
    });

    await this.logChange(
      'products',
      'product_options',
      option.id,
      'create',
      option
    );
    return option;
  }

  async createProductOptionValue(
    optionId: string,
    body: Record<string, unknown>
  ) {
    await this.assertProductOption(optionId);
    const value = await this.prisma.productOptionValue.create({
      data: {
        storeId: this.storeId(),
        optionId,
        value: this.requiredString(body, 'value'),
        position: this.optionalNumber(body, 'position') ?? 0,
      },
    });

    await this.logChange(
      'products',
      'product_option_values',
      value.id,
      'create',
      value
    );
    return value;
  }

  async listProductVariants(productId: string, query: RawQuery = {}) {
    await this.assertProduct(productId);
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), productId, isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.productVariant.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { title: 'asc' }],
        ...paging,
      }),
      this.prisma.productVariant.count({ where }),
    ]);

    // Stock is not a column on the variant — it is the ledger's
    // `available - committed` for this product/variant pair. Resolved in one
    // batch rather than per row.
    const sellable = await this.ledger?.sellableMap(
      items.map((variant) => ({ productId, variantId: variant.id }))
    );

    return {
      items: items.map((variant) => ({
        ...variant,
        stock: sellable?.get(`${productId}:${variant.id}`) ?? 0,
      })),
      total,
    };
  }

  async createProductVariant(productId: string, body: Record<string, unknown>) {
    await this.assertProduct(productId);
    const variant = await this.prisma.productVariant.create({
      data: {
        storeId: this.storeId(),
        productId,
        title: this.requiredString(body, 'title'),
        sku: this.optionalString(body, 'sku'),
        barcode: this.optionalString(body, 'barcode'),
        optionSnapshot: this.optionalJson(body, 'optionSnapshot'),
        price: this.requiredNumber(body, 'price'),
        compareAtPrice: this.optionalNumber(body, 'compareAtPrice'),
        costPrice: this.optionalNumber(body, 'costPrice'),
        currency: this.optionalString(body, 'currency') ?? 'INR',
        weightGrams: this.optionalNumber(body, 'weightGrams'),
        // The size this variant represents — "500 ml" as 500 + 'ml'. Optional
        // because not every catalogue sells by measure, and independent of
        // optionSnapshot: a 512GB phone still has a weight.
        unitOfMeasure: this.optionalString(body, 'unitOfMeasure'),
        unitQuantity: this.optionalNumber(body, 'unitQuantity'),
        imageUrl: this.optionalString(body, 'imageUrl'),
        priceEffectiveFrom: this.optionalDate(body, 'priceEffectiveFrom'),
        priceEffectiveTo: this.optionalDate(body, 'priceEffectiveTo'),
        isDefault: this.optionalBoolean(body, 'isDefault') ?? false,
      },
    });

    /*
     * A variant is a separate sellable thing, so it gets its own ledger line.
     * It starts at zero rather than inheriting the product's stock: the stock
     * already counted belongs to the variants that existed before this one, and
     * copying it would conjure inventory that is not on any shelf.
     */
    await this.ledger?.ensureItem(productId, variant.id, {
      sku: variant.sku,
      initialAvailable: this.optionalNumber(body, 'stock') ?? 0,
    });

    // The first variant on a product becomes its default even if the caller
    // did not ask, and the product's price starts mirroring it.
    await this.reconcileProductPricing(
      productId,
      variant.isDefault ? variant.id : undefined
    );

    await this.logChange(
      'products',
      'product_variants',
      variant.id,
      'create',
      variant
    );
    return variant;
  }

  async updateProductVariant(
    productId: string,
    variantId: string,
    body: Record<string, unknown>
  ) {
    await this.assertProductVariant(productId, variantId);
    const data: Prisma.ProductVariantUpdateInput = {};

    // title, price and currency are never blank on a saved variant, so absent
    // is the only "leave alone" they need. Everything else the drawer submits
    // can be cleared, and null has to mean cleared.
    this.assignOptionalString(data, body, 'title');
    this.assignOptionalNumber(data, body, 'price');
    this.assignOptionalString(data, body, 'currency');

    this.assignNullableString(data, body, 'sku');
    this.assignNullableString(data, body, 'barcode');
    this.assignNullableString(data, body, 'imageUrl');
    this.assignNullableString(data, body, 'unitOfMeasure');
    this.assignNullableNumber(data, body, 'compareAtPrice');
    this.assignNullableNumber(data, body, 'costPrice');
    this.assignNullableNumber(data, body, 'weightGrams');
    this.assignNullableNumber(data, body, 'unitQuantity');
    this.assignNullableDate(data, body, 'priceEffectiveFrom');
    this.assignNullableDate(data, body, 'priceEffectiveTo');

    this.assignOptionalBoolean(data, body, 'isDefault');
    this.assignOptionalNumber(data, body, 'sortOrder');
    if (body.optionSnapshot !== undefined) {
      // Removing every option from a variant sends null. A bare null is not a
      // clear in Prisma's Json API — it has to be Prisma.DbNull, or the column
      // keeps whatever it held.
      data.optionSnapshot =
        body.optionSnapshot === null
          ? Prisma.DbNull
          : this.optionalJson(body, 'optionSnapshot');
    }

    if (Object.keys(data).length === 0) {
      throw new BadRequestException('at least one variant field is required');
    }

    const variant = await this.prisma.productVariant.update({
      where: { id: variantId },
      data,
    });

    // `stock` is not a column on the variant — it lives in the ledger, so it is
    // pulled out of the body and applied there rather than passed to Prisma.
    if (body['stock'] !== undefined) {
      await this.ledger?.setAvailable(
        productId,
        variantId,
        this.optionalNumber(body, 'stock') ?? 0,
        'Merchant stock update (variant)'
      );
    }

    await this.reconcileProductPricing(
      productId,
      data.isDefault === true ? variant.id : undefined
    );

    await this.logChange(
      'products',
      'product_variants',
      variant.id,
      'update',
      variant
    );
    return variant;
  }

  /**
   * Re-establish the two rules that tie a product to its variants.
   *
   *   1. Exactly one active variant is the default.
   *   2. The product's own price and size columns mirror that default.
   *
   * Rule 2 is the important one. Product and ProductVariant store the same two
   * numbers with the same meaning — compareAtPrice is the MRP, price is what
   * the shopper pays — and a cart line resolves as
   * `variant?.price ?? product.price`. So once a product has variants, its own
   * columns are a cache of the default variant, not a second opinion.
   *
   * They cannot simply be dropped: `product.price` is the column the category
   * page prints, and the one price sorting and price-range filters read
   * (see prisma-products.repository.ts). Deleting it would turn two indexed
   * predicates into correlated subqueries on every catalogue query. So it
   * stays a real column and this is the only thing allowed to write it.
   *
   * Called after every variant mutation. Silent no-op for a product with no
   * variants left — it keeps the price it last sold at rather than dropping to
   * zero.
   *
   * @param preferDefaultId the variant the caller just marked default, if any.
   */
  private async reconcileProductPricing(
    productId: string,
    preferDefaultId?: string
  ): Promise<void> {
    const variants = await this.prisma.productVariant.findMany({
      where: { productId, storeId: this.storeId(), isActive: true },
      orderBy: [{ sortOrder: 'asc' }, { createdAt: 'asc' }],
      select: {
        id: true,
        isDefault: true,
        price: true,
        compareAtPrice: true,
        unitOfMeasure: true,
        unitQuantity: true,
      },
    });

    if (!variants.length) return;

    // The caller's choice wins; then whatever was already marked; then the
    // first. The last fallback is what stops a product ever having variants
    // and no default — a state the storefront renders as "no variant
    // selected", falling back to the product price nobody set.
    const preferred =
      variants.find((v) => v.id === preferDefaultId) ??
      variants.find((v) => v.isDefault) ??
      variants[0];

    const operations: Prisma.PrismaPromise<unknown>[] = [];

    const staleDefaults = variants.filter((v) => v.id !== preferred.id && v.isDefault);
    if (staleDefaults.length) {
      operations.push(
        this.prisma.productVariant.updateMany({
          where: { id: { in: staleDefaults.map((v) => v.id) } },
          data: { isDefault: false },
        })
      );
    }

    if (!preferred.isDefault) {
      operations.push(
        this.prisma.productVariant.update({
          where: { id: preferred.id },
          data: { isDefault: true },
        })
      );
    }

    const mrp = preferred.compareAtPrice ?? preferred.price;
    operations.push(
      this.prisma.product.update({
        where: { id: productId },
        data: {
          price: preferred.price,
          compareAtPrice: mrp,
          // Size rides along for the same reason as price: the PDP prints the
          // product's size under the price, and a "500 ml" there while the
          // selected chip says 1 L is the same contradiction in a different
          // field. Only mirrored when the default variant actually has a
          // size — an options-only variant leaves whatever the product had.
          ...(preferred.unitOfMeasure && preferred.unitQuantity
            ? {
                unitOfMeasure: preferred.unitOfMeasure,
                unitQuantity: preferred.unitQuantity,
              }
            : {}),
        },
      })
    );

    await this.prisma.$transaction(operations);

    // Stock is a sum over the variants rather than a mirror of the default, so
    // it is recomputed here too — adding, removing or deactivating a variant
    // all change the product total.
    await this.ledger?.syncProductCache(productId);
  }

  async deleteProductVariant(productId: string, variantId: string) {
    await this.assertProductVariant(productId, variantId);
    const variant = await this.prisma.productVariant.update({
      where: { id: variantId },
      data: { isActive: false },
    });

    // Its stock stops counting toward the product total the moment it is no
    // longer sellable. The level rows are left intact — the movements ledger
    // should still be able to explain where that stock went.
    await this.ledger?.deactivateItem(productId, variantId);

    // Removing the default hands the role to whatever is left, and the
    // product's price follows it.
    await this.reconcileProductPricing(productId);

    await this.logChange(
      'products',
      'product_variants',
      variant.id,
      'delete',
      variant
    );
    return variant;
  }

  async listProductImages(productId: string, query: RawQuery = {}) {
    await this.assertProduct(productId);
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), productId, isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.productImage.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { createdAt: 'asc' }],
        ...paging,
      }),
      this.prisma.productImage.count({ where }),
    ]);

    return { items, total };
  }

  async createProductImage(productId: string, body: Record<string, unknown>) {
    await this.assertProduct(productId);
    const image = await this.prisma.productImage.create({
      data: {
        storeId: this.storeId(),
        productId,
        url: this.requiredString(body, 'url'),
        altText: this.optionalString(body, 'altText') ?? '',
        sortOrder: this.optionalNumber(body, 'sortOrder') ?? 0,
        // Manually-pasted URLs (e.g. an externally-hosted image) don't
        // occupy this merchant's local-disk quota, unlike a real upload.
        sizeBytes: 0,
      },
    });

    await this.logChange(
      'products',
      'product_images',
      image.id,
      'create',
      image
    );
    return image;
  }

  async uploadProductImage(
    productId: string,
    file: Express.Multer.File,
    body: Record<string, unknown>
  ) {
    await this.assertProduct(productId);
    if (!file) {
      throw new BadRequestException('file is required');
    }

    const orgId = this.organizationId();
    const settings = await this.images.get(orgId);

    if (settings.maxImagesPerProduct != null) {
      const liveCount = await this.prisma.productImage.count({
        where: { productId, isActive: true },
      });
      if (liveCount >= settings.maxImagesPerProduct) {
        throw new BadRequestException(
          `This product already has the maximum of ${settings.maxImagesPerProduct} images allowed.`
        );
      }
    }

    const compressionPercent = this.optionalNumber(body, 'compressionPercent') ?? settings.compressionDefault;
    let buffer = file.buffer;
    let sizeBytes = file.size;
    if (compressionPercent > 0 && isCompressible(file.mimetype)) {
      const compressed = await compressImage(file.buffer, file.mimetype, compressionPercent);
      buffer = compressed.buffer;
      sizeBytes = compressed.sizeBytes;
    }

    if (settings.maxImageSizeBytes != null && sizeBytes > settings.maxImageSizeBytes) {
      throw new BadRequestException(
        `This image is ${sizeBytes} bytes, over the ${settings.maxImageSizeBytes} byte limit per image` +
          (compressionPercent > 0 ? '' : ' — try a higher compression level.')
      );
    }

    // The media stack owns storage routing, quota accounting and the asset
    // registry once it is wired in (production). It writes to whichever
    // backend the org is currently on, which is what keeps product images
    // working across a storage switch without moving anything.
    if (this.mediaUpload) {
      const asset = await this.mediaUpload.uploadProxied({
        organizationId: orgId,
        storeId: this.storeId(),
        ownerType: 'product',
        ownerId: productId,
        fileName: file.originalname,
        declaredMimeType: file.mimetype,
        // Compression already ran above, against the org's configured default;
        // passing 0 here stops it happening twice.
        buffer,
        compressionPercent: 0,
      });

      const created = await this.prisma.productImage.create({
        data: {
          storeId: this.storeId(),
          productId,
          url: asset.publicUrl ?? '',
          storageKey: asset.storageKey,
          mediaAssetId: asset.id,
          sizeBytes: asset.sizeBytes,
          altText: this.optionalString(body, 'altText') ?? '',
          sortOrder: this.optionalNumber(body, 'sortOrder') ?? 0,
        },
      });

      await this.logChange('products', 'product_images', created.id, 'create', created);
      return { ...created, quotaWarning: asset.quotaWarning };
    }

    const { warning } = await this.quota.assertCanUpload(orgId, sizeBytes);

    const key = `${orgId}/${productId}/${randomUUID()}-${this.sanitizeFilename(file.originalname)}`;
    const { url, storageKey } = await this.storage.save(buffer, key, file.mimetype);

    const image = await this.prisma.productImage.create({
      data: {
        storeId: this.storeId(),
        productId,
        url,
        storageKey,
        sizeBytes,
        altText: this.optionalString(body, 'altText') ?? '',
        sortOrder: this.optionalNumber(body, 'sortOrder') ?? 0,
      },
    });

    await this.logChange('products', 'product_images', image.id, 'create', image);
    return { ...image, quotaWarning: warning };
  }

  async getImageSettings() {
    return this.images.get(this.organizationId());
  }

  async updateImageSettings(body: Record<string, unknown>) {
    const input: UpdateOrgImageSettingsInput = {};
    const compression = this.optionalNumber(body, 'compressionDefault');
    if (compression !== undefined) input.compressionDefault = compression;

    if ('maxImageSizeBytes' in body) {
      input.maxImageSizeBytes = body['maxImageSizeBytes'] == null ? null : Number(body['maxImageSizeBytes']);
    }
    if ('maxImagesPerProduct' in body) {
      input.maxImagesPerProduct =
        body['maxImagesPerProduct'] == null ? null : Number(body['maxImagesPerProduct']);
    }

    return this.images.update(this.organizationId(), input);
  }

  async updateProductImage(imageId: string, body: Record<string, unknown>) {
    await this.assertProductImage(imageId);
    const data: Prisma.ProductImageUpdateInput = {};

    this.assignOptionalString(data, body, 'url');
    this.assignOptionalString(data, body, 'altText');
    this.assignOptionalNumber(data, body, 'sortOrder');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException('at least one image field is required');
    }

    const image = await this.prisma.productImage.update({
      where: { id: imageId },
      data,
    });

    await this.logChange(
      'products',
      'product_images',
      image.id,
      'update',
      image
    );
    return image;
  }

  async deleteProductImage(imageId: string) {
    await this.assertProductImage(imageId);
    const image = await this.prisma.productImage.update({
      where: { id: imageId },
      data: { isActive: false, trashedAt: new Date() },
    });

    /*
     * The catalogue row is only half of a deleted image. The object in storage
     * belongs to a MediaAsset, and until that is trashed too the bytes keep
     * counting against the merchant's quota and no sweep will ever collect
     * them — the retention cron looks for trashed assets, and this one would
     * still say `active`.
     *
     * That was the bug: deleting an image freed nothing, ever, and 30 days
     * later the object was purged while the asset row lived on pointing at
     * something that no longer existed.
     */
    if (image.mediaAssetId && this.mediaAssets) {
      await this.mediaAssets.trash(image.mediaAssetId, this.organizationId(), 'merchant');
    }

    await this.logChange(
      'products',
      'product_images',
      image.id,
      'delete',
      image
    );
    return image;
  }

  // Not `assertProductImage` — that filters isActive:true, and a trashed
  // image (what this purges) is exactly isActive:false.
  async hardDeleteProductImage(imageId: string) {
    const image = await this.prisma.productImage.findFirst({
      where: { id: imageId, storeId: this.storeId() },
    });
    if (!image) {
      throw new NotFoundException('product image not found');
    }

    /*
     * Routed through MediaAssetService when the image has an asset, because it
     * is the only thing that decrements the usage counter and the only thing
     * that knows which backend actually holds the object. The direct path
     * below stays for images that predate the media registry.
     */
    if (image.mediaAssetId && this.mediaAssets) {
      await this.mediaAssets.purge(image.mediaAssetId);
      await this.prisma.productImage.delete({ where: { id: image.id } });
    } else {
      await purgeProductImage(this.prisma, this.storage, image);
    }

    await this.logChange('products', 'product_images', imageId, 'delete', image);
    return { id: imageId, purged: true };
  }

  async listTrashedProductImages(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = {
      store: { organizationId: this.organizationId() },
      isActive: false,
      trashedAt: { not: null },
    };
    const [rows, total] = await Promise.all([
      this.prisma.productImage.findMany({
        where,
        orderBy: { trashedAt: 'desc' },
        include: { product: { select: { id: true, name: true } } },
        ...paging,
      }),
      this.prisma.productImage.count({ where }),
    ]);

    const items = rows.map((row) => ({
      id: row.id,
      productId: row.product.id,
      productName: row.product.name,
      url: row.url,
      altText: row.altText,
      sizeBytes: row.sizeBytes,
      trashedAt: row.trashedAt?.toISOString() ?? '',
    }));

    return { items, total };
  }

  async getImageUsage() {
    return this.quota.getUsage(this.organizationId());
  }

  async listInventoryLocations(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.inventoryLocation.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { name: 'asc' }],
        ...paging,
      }),
      this.prisma.inventoryLocation.count({ where }),
    ]);

    return { items, total };
  }

  async createInventoryLocation(body: Record<string, unknown>) {
    const location = await this.prisma.inventoryLocation.create({
      data: {
        storeId: this.storeId(),
        name: this.requiredString(body, 'name'),
        code: this.requiredString(body, 'code'),
      },
    });

    await this.logChange(
      'inventory',
      'locations',
      location.id,
      'create',
      location
    );
    return location;
  }

  async createInventoryItem(body: Record<string, unknown>) {
    const productId = this.requiredString(body, 'productId');
    await this.assertProduct(productId);
    const item = await this.prisma.inventoryItem.create({
      data: {
        storeId: this.storeId(),
        productId,
        variantId: this.optionalString(body, 'variantId'),
        sku: this.optionalString(body, 'sku'),
        tracked: this.optionalBoolean(body, 'tracked') ?? true,
      },
    });

    await this.logChange(
      'inventory',
      'inventory_items',
      item.id,
      'create',
      item
    );
    return item;
  }

  async listInventoryItems(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = {
      storeId: this.storeId(),
      isActive: true,
      ...(query.productId ? { productId: query.productId } : {}),
      ...(query.variantId ? { variantId: query.variantId } : {}),
    };
    const [items, total] = await Promise.all([
      this.prisma.inventoryItem.findMany({
        where,
        orderBy: [{ createdAt: 'desc' }],
        ...paging,
      }),
      this.prisma.inventoryItem.count({ where }),
    ]);

    return { items, total };
  }

  async updateInventoryItem(
    inventoryItemId: string,
    body: Record<string, unknown>
  ) {
    await this.assertInventoryItem(inventoryItemId);
    const data: Prisma.InventoryItemUpdateInput = {};

    this.assignOptionalString(data, body, 'sku');
    this.assignOptionalBoolean(data, body, 'tracked');
    this.assignOptionalNumber(data, body, 'sortOrder');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException(
        'at least one inventory item field is required'
      );
    }

    const item = await this.prisma.inventoryItem.update({
      where: { id: inventoryItemId },
      data,
    });

    await this.logChange(
      'inventory',
      'inventory_items',
      item.id,
      'update',
      item
    );
    return item;
  }

  async deleteInventoryItem(inventoryItemId: string) {
    await this.assertInventoryItem(inventoryItemId);
    const item = await this.prisma.inventoryItem.update({
      where: { id: inventoryItemId },
      data: { isActive: false },
    });

    await this.logChange(
      'inventory',
      'inventory_items',
      item.id,
      'delete',
      item
    );
    return item;
  }

  async createInventoryLevel(body: Record<string, unknown>) {
    const inventoryItemId = this.requiredString(body, 'inventoryItemId');
    const locationId = this.requiredString(body, 'locationId');
    await this.assertInventoryItem(inventoryItemId);
    await this.assertInventoryLocation(locationId);
    const level = await this.prisma.inventoryLevel.create({
      data: {
        storeId: this.storeId(),
        inventoryItemId,
        locationId,
        available: this.optionalNumber(body, 'available') ?? 0,
        committed: this.optionalNumber(body, 'committed') ?? 0,
      },
    });

    await this.logChange(
      'inventory',
      'inventory_levels',
      level.id,
      'create',
      level
    );
    return level;
  }

  async listInventoryLevels(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = {
      storeId: this.storeId(),
      isActive: true,
      ...(query.inventoryItemId
        ? { inventoryItemId: query.inventoryItemId }
        : {}),
      ...(query.locationId ? { locationId: query.locationId } : {}),
    };
    const [items, total] = await Promise.all([
      this.prisma.inventoryLevel.findMany({
        where,
        orderBy: [{ createdAt: 'desc' }],
        ...paging,
      }),
      this.prisma.inventoryLevel.count({ where }),
    ]);

    return { items, total };
  }

  async updateInventoryLevel(
    inventoryLevelId: string,
    body: Record<string, unknown>
  ) {
    await this.assertInventoryLevel(inventoryLevelId);
    const data: Prisma.InventoryLevelUpdateInput = {};

    this.assignOptionalNumber(data, body, 'available');
    this.assignOptionalNumber(data, body, 'committed');
    this.assignOptionalNumber(data, body, 'sortOrder');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException(
        'at least one inventory level field is required'
      );
    }

    const level = await this.prisma.inventoryLevel.update({
      where: { id: inventoryLevelId },
      data,
    });

    await this.logChange(
      'inventory',
      'inventory_levels',
      level.id,
      'update',
      level
    );
    return level;
  }

  async deleteInventoryLevel(inventoryLevelId: string) {
    await this.assertInventoryLevel(inventoryLevelId);
    const level = await this.prisma.inventoryLevel.update({
      where: { id: inventoryLevelId },
      data: { isActive: false },
    });

    await this.logChange(
      'inventory',
      'inventory_levels',
      level.id,
      'delete',
      level
    );
    return level;
  }

  async reserveInventoryLevel(
    inventoryLevelId: string,
    body: Record<string, unknown>
  ) {
    const quantity = this.requiredPositiveInteger(body, 'quantity');
    const level = await this.findInventoryLevelForReservation(inventoryLevelId);
    const availableToReserve = level.available - level.committed;

    if (quantity > availableToReserve) {
      throw new BadRequestException(
        'quantity exceeds uncommitted available stock'
      );
    }

    const result = await this.prisma.$transaction(async (tx) => {
      // First statement in the transaction, deliberately: audit.fn_log_change()
      // reads this setting at the moment it fires, so anything written before
      // this point is attributed to `system`.
      await this.auditTx?.publishActor(tx);
      const updated = await tx.inventoryLevel.update({
        where: { id: inventoryLevelId },
        data: { committed: { increment: quantity } },
      });
      const movement = await tx.inventoryMovement.create({
        data: {
          storeId: this.storeId(),
          inventoryItemId: level.inventoryItemId,
          locationId: level.locationId,
          changeType: 'reserve',
          quantityDelta: 0,
          reason: this.optionalString(body, 'reason'),
          referenceType:
            this.optionalString(body, 'referenceType') ?? 'reservation',
          referenceId: this.optionalString(body, 'referenceId'),
        },
      });

      return { level: updated, movement };
    });

    await this.logChange(
      'inventory',
      'inventory_levels',
      result.level.id,
      'reserve',
      result
    );
    return result;
  }

  async releaseInventoryLevel(
    inventoryLevelId: string,
    body: Record<string, unknown>
  ) {
    const quantity = this.requiredPositiveInteger(body, 'quantity');
    const level = await this.findInventoryLevelForReservation(inventoryLevelId);

    if (quantity > level.committed) {
      throw new BadRequestException('quantity exceeds committed stock');
    }

    const result = await this.prisma.$transaction(async (tx) => {
      // First statement in the transaction, deliberately: audit.fn_log_change()
      // reads this setting at the moment it fires, so anything written before
      // this point is attributed to `system`.
      await this.auditTx?.publishActor(tx);
      const updated = await tx.inventoryLevel.update({
        where: { id: inventoryLevelId },
        data: { committed: { decrement: quantity } },
      });
      const movement = await tx.inventoryMovement.create({
        data: {
          storeId: this.storeId(),
          inventoryItemId: level.inventoryItemId,
          locationId: level.locationId,
          changeType: 'release',
          quantityDelta: 0,
          reason: this.optionalString(body, 'reason'),
          referenceType:
            this.optionalString(body, 'referenceType') ?? 'reservation',
          referenceId: this.optionalString(body, 'referenceId'),
        },
      });

      return { level: updated, movement };
    });

    await this.logChange(
      'inventory',
      'inventory_levels',
      result.level.id,
      'release',
      result
    );
    return result;
  }

  async commitInventoryLevel(
    inventoryLevelId: string,
    body: Record<string, unknown>
  ) {
    const quantity = this.requiredPositiveInteger(body, 'quantity');
    const level = await this.findInventoryLevelForReservation(inventoryLevelId);

    if (quantity > level.committed) {
      throw new BadRequestException('quantity exceeds committed stock');
    }

    if (quantity > level.available) {
      throw new BadRequestException('quantity exceeds available stock');
    }

    const result = await this.prisma.$transaction(async (tx) => {
      // First statement in the transaction, deliberately: audit.fn_log_change()
      // reads this setting at the moment it fires, so anything written before
      // this point is attributed to `system`.
      await this.auditTx?.publishActor(tx);
      const updated = await tx.inventoryLevel.update({
        where: { id: inventoryLevelId },
        data: {
          available: { decrement: quantity },
          committed: { decrement: quantity },
        },
      });
      const movement = await tx.inventoryMovement.create({
        data: {
          storeId: this.storeId(),
          inventoryItemId: level.inventoryItemId,
          locationId: level.locationId,
          changeType: 'commit',
          quantityDelta: -quantity,
          reason: this.optionalString(body, 'reason'),
          referenceType: this.optionalString(body, 'referenceType') ?? 'order',
          referenceId: this.optionalString(body, 'referenceId'),
        },
      });

      return { level: updated, movement };
    });

    await this.logChange(
      'inventory',
      'inventory_levels',
      result.level.id,
      'commit',
      result
    );
    return result;
  }

  async createInventoryMovement(body: Record<string, unknown>) {
    const inventoryItemId = this.requiredString(body, 'inventoryItemId');
    const locationId = this.optionalString(body, 'locationId');
    const quantityDelta = this.requiredNumber(body, 'quantityDelta');
    await this.assertInventoryItem(inventoryItemId);
    if (locationId) {
      await this.assertInventoryLocation(locationId);
    }

    const data = {
      storeId: this.storeId(),
      inventoryItemId,
      locationId,
      changeType: this.requiredString(body, 'changeType'),
      quantityDelta,
      reason: this.optionalString(body, 'reason'),
      referenceType: this.optionalString(body, 'referenceType'),
      referenceId: this.optionalString(body, 'referenceId'),
    };
    const movement = locationId
      ? await this.prisma.$transaction(async (tx) => {
        // First statement in the transaction, deliberately: audit.fn_log_change()
        // reads this setting at the moment it fires, so anything written before
        // this point is attributed to `system`.
        await this.auditTx?.publishActor(tx);
          const created = await tx.inventoryMovement.create({ data });
          await tx.inventoryLevel.updateMany({
            where: {
              storeId: this.storeId(),
              inventoryItemId,
              locationId,
              isActive: true,
            },
            data: { available: { increment: quantityDelta } },
          });
          return created;
        })
      : await this.prisma.inventoryMovement.create({
          data,
        });

    await this.logChange(
      'inventory',
      'inventory_movements',
      movement.id,
      'create',
      movement
    );
    return movement;
  }

  async listInventoryMovements(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = {
      storeId: this.storeId(),
      isActive: true,
      ...(query.inventoryItemId
        ? { inventoryItemId: query.inventoryItemId }
        : {}),
      ...(query.locationId ? { locationId: query.locationId } : {}),
      ...(query.referenceType ? { referenceType: query.referenceType } : {}),
      ...(query.referenceId ? { referenceId: query.referenceId } : {}),
    };
    const [items, total] = await Promise.all([
      this.prisma.inventoryMovement.findMany({
        where,
        orderBy: [{ createdAt: 'desc' }],
        ...paging,
      }),
      this.prisma.inventoryMovement.count({ where }),
    ]);

    return { items, total };
  }

  async listCountries(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.country.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { name: 'asc' }],
        ...paging,
      }),
      this.prisma.country.count({ where }),
    ]);

    return { items, total };
  }

  async createCountry(body: Record<string, unknown>) {
    const item = await this.prisma.country.create({
      data: {
        iso2: this.requiredString(body, 'iso2').toUpperCase(),
        iso3: this.optionalString(body, 'iso3')?.toUpperCase(),
        name: this.requiredString(body, 'name'),
        phoneCode: this.optionalString(body, 'phoneCode'),
        currencyCode: this.optionalString(body, 'currencyCode')?.toUpperCase(),
      },
    });

    await this.logChange('master', 'countries', item.id, 'create', item);
    return item;
  }

  async listStates(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = {
      isActive: true,
      ...(query.countryId ? { countryId: query.countryId } : {}),
    };
    const [items, total] = await Promise.all([
      this.prisma.state.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { name: 'asc' }],
        ...paging,
      }),
      this.prisma.state.count({ where }),
    ]);

    return { items, total };
  }

  async createState(body: Record<string, unknown>) {
    const countryId = this.requiredString(body, 'countryId');
    await this.assertCountry(countryId);
    const item = await this.prisma.state.create({
      data: {
        countryId,
        code: this.requiredString(body, 'code').toUpperCase(),
        name: this.requiredString(body, 'name'),
      },
    });

    await this.logChange('master', 'states', item.id, 'create', item);
    return item;
  }

  async listCities(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = {
      isActive: true,
      ...(query.stateId ? { stateId: query.stateId } : {}),
    };
    const [items, total] = await Promise.all([
      this.prisma.city.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { name: 'asc' }],
        ...paging,
      }),
      this.prisma.city.count({ where }),
    ]);

    return { items, total };
  }

  async createCity(body: Record<string, unknown>) {
    const stateId = this.requiredString(body, 'stateId');
    await this.assertState(stateId);
    const item = await this.prisma.city.create({
      data: {
        stateId,
        name: this.requiredString(body, 'name'),
      },
    });

    await this.logChange('master', 'cities', item.id, 'create', item);
    return item;
  }

  async listCurrencies(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.currency.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { code: 'asc' }],
        ...paging,
      }),
      this.prisma.currency.count({ where }),
    ]);

    return { items, total };
  }

  async createCurrency(body: Record<string, unknown>) {
    const item = await this.prisma.currency.create({
      data: {
        code: this.requiredString(body, 'code').toUpperCase(),
        name: this.requiredString(body, 'name'),
        symbol: this.requiredString(body, 'symbol'),
        decimalPlaces: this.optionalNumber(body, 'decimalPlaces') ?? 2,
      },
    });

    await this.logChange('master', 'currencies', item.id, 'create', item);
    return item;
  }

  async listTaxSlabProfiles(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.taxSlabProfile.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { igstRateBps: 'asc' }],
        ...paging,
      }),
      this.prisma.taxSlabProfile.count({ where }),
    ]);

    return { items, total };
  }

  // CGST + SGST must always equal IGST — the same total tax liability,
  // just split differently depending on whether the sale is intra- or
  // inter-state. Enforced here (not a DB constraint) so the error message
  // can be specific, matching the admin UI's "Live Guardrail Validator".
  private assertGstSplitValid(igstRateBps: number, cgstRateBps: number, sgstRateBps: number): void {
    if (cgstRateBps + sgstRateBps !== igstRateBps) {
      throw new BadRequestException(
        `CGST (${cgstRateBps}) + SGST (${sgstRateBps}) must equal IGST (${igstRateBps})`
      );
    }
  }

  async createTaxSlabProfile(body: Record<string, unknown>) {
    const igstRateBps = this.requiredNumber(body, 'igstRateBps');
    const cgstRateBps = this.requiredNumber(body, 'cgstRateBps');
    const sgstRateBps = this.requiredNumber(body, 'sgstRateBps');
    this.assertGstSplitValid(igstRateBps, cgstRateBps, sgstRateBps);

    const item = await this.prisma.taxSlabProfile.create({
      data: {
        storeId: this.storeId(),
        name: this.requiredString(body, 'name'),
        code: this.requiredString(body, 'code').toUpperCase(),
        hsnGroup: this.optionalString(body, 'hsnGroup'),
        igstRateBps,
        cgstRateBps,
        sgstRateBps,
      },
    });

    await this.logChange('master', 'tax_slab_profiles', item.id, 'create', item);
    return item;
  }

  async updateTaxSlabProfile(
    taxProfileId: string,
    body: Record<string, unknown>
  ) {
    const existing = await this.assertTaxSlabProfile(taxProfileId);
    const igstRateBps = this.optionalNumber(body, 'igstRateBps') ?? existing.igstRateBps;
    const cgstRateBps = this.optionalNumber(body, 'cgstRateBps') ?? existing.cgstRateBps;
    const sgstRateBps = this.optionalNumber(body, 'sgstRateBps') ?? existing.sgstRateBps;
    this.assertGstSplitValid(igstRateBps, cgstRateBps, sgstRateBps);

    const data: Prisma.TaxSlabProfileUpdateInput = {
      igstRateBps,
      cgstRateBps,
      sgstRateBps,
    };

    this.assignOptionalString(data, body, 'name');
    this.assignOptionalString(data, body, 'hsnGroup');
    this.assignOptionalBoolean(data, body, 'isActive');
    this.assignOptionalNumber(data, body, 'sortOrder');

    const item = await this.prisma.taxSlabProfile.update({
      where: { id: taxProfileId },
      data,
    });

    await this.logChange('master', 'tax_slab_profiles', item.id, 'update', item);
    return item;
  }

  async deleteTaxSlabProfile(taxProfileId: string) {
    await this.assertTaxSlabProfile(taxProfileId);
    const item = await this.prisma.taxSlabProfile.update({
      where: { id: taxProfileId },
      data: { isActive: false },
    });

    await this.logChange('master', 'tax_slab_profiles', item.id, 'delete', item);
    return item;
  }

  async getGstSettings() {
    const store = await this.prisma.store.findUniqueOrThrow({
      where: { id: this.storeId() },
      select: { gstin: true, defaultTaxInclusive: true },
    });
    return store;
  }

  async updateGstSettings(body: Record<string, unknown>) {
    const data: Prisma.StoreUpdateInput = {};
    this.assignOptionalString(data, body, 'gstin');
    this.assignOptionalBoolean(data, body, 'defaultTaxInclusive');

    const store = await this.prisma.store.update({
      where: { id: this.storeId() },
      data,
      select: { gstin: true, defaultTaxInclusive: true },
    });

    await this.logChange('store', 'stores', this.storeId(), 'update', store);
    return store;
  }

  async getTcsSettings() {
    const store = await this.prisma.store.findUniqueOrThrow({
      where: { id: this.storeId() },
      select: { tcsRateBps: true, tcsThresholdPaise: true },
    });
    return store;
  }

  async updateTcsSettings(body: Record<string, unknown>) {
    const data: Prisma.StoreUpdateInput = {};
    this.assignOptionalNumber(data, body, 'tcsRateBps');
    this.assignOptionalNumber(data, body, 'tcsThresholdPaise');

    const store = await this.prisma.store.update({
      where: { id: this.storeId() },
      data,
      select: { tcsRateBps: true, tcsThresholdPaise: true },
    });

    await this.logChange('store', 'stores', this.storeId(), 'update', store);
    return store;
  }

  // Aggregates already-computed per-order-item GST amounts — no
  // recalculation, just grouping what checkout already stored on each
  // OrderItem row (see checkout.service.ts). Grouped by the product's
  // CURRENT tax profile name (a best-effort label — an order item's own
  // frozen rate snapshot is what the totals are actually summed from, not
  // the live profile, matching the "frozen at checkout time" invariant
  // documented on CheckoutLineItem/OrderItem in schema.prisma).
  async getTaxReport(query: RawQuery = {}) {
    const { take, skip } = this.paging(query);

    const where: Prisma.OrderWhereInput = { storeId: this.storeId(), isActive: true };
    if (query.from || query.to) {
      where.placedOn = {
        ...(query.from ? { gte: new Date(query.from) } : {}),
        ...(query.to ? { lte: new Date(query.to) } : {}),
      };
    }
    if (query.type === 'intra') where.igstTotal = 0;
    if (query.type === 'inter') where.igstTotal = { gt: 0 };

    // OrderItem carries its own storeId (no direct Prisma relation to
    // Order — same "flat FK, no nested select" convention used
    // throughout this service), so the slab filter is a plain lookup
    // rather than a nested `some` filter.
    if (query.igstRateBps !== undefined && query.igstRateBps !== '') {
      const rate = Number(query.igstRateBps);
      const matches = await this.prisma.orderItem.findMany({
        where: { storeId: this.storeId(), igstRateBps: rate, isActive: true },
        select: { orderId: true },
      });
      where.id = { in: [...new Set(matches.map((m) => m.orderId))] };
    }

    const [total, orders, summaryAgg] = await Promise.all([
      this.prisma.order.count({ where }),
      this.prisma.order.findMany({ where, orderBy: { placedOn: 'desc' }, take, skip }),
      this.prisma.order.aggregate({
        where,
        _sum: { subtotal: true, discountTotal: true, cgstTotal: true, sgstTotal: true, igstTotal: true, tcsTotal: true },
      }),
    ]);

    const items = orders.map((o) => {
      const shipping = (o.shippingAddress ?? {}) as { state?: string };
      return {
        orderId: o.id,
        orderDate: o.placedOn.toISOString(),
        state: shipping.state ?? '—',
        isInterState: o.igstTotal > 0,
        taxableValue: o.subtotal - o.discountTotal,
        cgstTotal: o.cgstTotal,
        sgstTotal: o.sgstTotal,
        igstTotal: o.igstTotal,
        totalTax: o.cgstTotal + o.sgstTotal + o.igstTotal,
        grandTotal: o.grandTotal,
      };
    });

    const sum = summaryAgg._sum;
    return {
      items,
      total,
      summary: {
        taxableValue: (sum.subtotal ?? 0) - (sum.discountTotal ?? 0),
        cgstTotal: sum.cgstTotal ?? 0,
        sgstTotal: sum.sgstTotal ?? 0,
        igstTotal: sum.igstTotal ?? 0,
        totalTax: (sum.cgstTotal ?? 0) + (sum.sgstTotal ?? 0) + (sum.igstTotal ?? 0),
        tcsTotal: sum.tcsTotal ?? 0,
      },
    };
  }

  async listStoreSettings(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.storeSetting.findMany({
        where,
        orderBy: [{ key: 'asc' }],
        ...paging,
      }),
      this.prisma.storeSetting.count({ where }),
    ]);

    return { items, total };
  }

  async upsertStoreSetting(body: Record<string, unknown>) {
    const key = this.requiredString(body, 'key');
    const value = this.requiredJson(body, 'value');
    const setting = await this.prisma.storeSetting.upsert({
      where: { storeId_key: { storeId: this.storeId(), key } },
      create: { storeId: this.storeId(), key, value },
      update: { value },
    });

    await this.logChange(
      'store',
      'store_settings',
      setting.id,
      'upsert',
      setting
    );
    return setting;
  }

  async listStoreDomains(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.storeDomain.findMany({
        where,
        orderBy: [{ isPrimary: 'desc' }, { domain: 'asc' }],
        ...paging,
      }),
      this.prisma.storeDomain.count({ where }),
    ]);

    return { items, total };
  }

  async createStoreDomain(body: Record<string, unknown>) {
    const domain = await this.prisma.storeDomain.create({
      data: {
        storeId: this.storeId(),
        domain: this.requiredString(body, 'domain').toLowerCase(),
        isPrimary: this.optionalBoolean(body, 'isPrimary') ?? false,
        verificationStatus:
          this.optionalString(body, 'verificationStatus') ?? 'pending',
      },
    });

    await this.logChange('store', 'store_domains', domain.id, 'create', domain);
    return domain;
  }

  async updateStoreDomain(domainId: string, body: Record<string, unknown>) {
    await this.assertStoreDomain(domainId);
    const data: Prisma.StoreDomainUpdateInput = {};
    const domain = this.optionalString(body, 'domain');

    if (domain !== undefined) data.domain = domain.toLowerCase();
    this.assignOptionalBoolean(data, body, 'isPrimary');
    this.assignOptionalString(data, body, 'verificationStatus');
    this.assignOptionalNumber(data, body, 'sortOrder');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException('at least one domain field is required');
    }

    const item = await this.prisma.storeDomain.update({
      where: { id: domainId },
      data,
    });

    await this.logChange('store', 'store_domains', item.id, 'update', item);
    return item;
  }

  async deleteStoreDomain(domainId: string) {
    await this.assertStoreDomain(domainId);
    const item = await this.prisma.storeDomain.update({
      where: { id: domainId },
      data: { isActive: false },
    });

    await this.logChange('store', 'store_domains', item.id, 'delete', item);
    return item;
  }

  async listThemeSettings(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.themeSetting.findMany({
        where,
        orderBy: [{ isPublished: 'desc' }, { themeKey: 'asc' }],
        ...paging,
      }),
      this.prisma.themeSetting.count({ where }),
    ]);

    return { items, total };
  }

  async upsertThemeSetting(body: Record<string, unknown>) {
    const themeKey = this.requiredString(body, 'themeKey');
    const settings = this.requiredJson(body, 'settings');
    const item = await this.prisma.themeSetting.upsert({
      where: {
        id: this.optionalString(body, 'id') ?? `${this.storeId()}-${themeKey}`,
      },
      create: {
        id: this.optionalString(body, 'id') ?? `${this.storeId()}-${themeKey}`,
        storeId: this.storeId(),
        themeKey,
        settings,
        isPublished: this.optionalBoolean(body, 'isPublished') ?? false,
      },
      update: {
        settings,
        isPublished: this.optionalBoolean(body, 'isPublished') ?? false,
      },
    });

    await this.logChange('store', 'theme_settings', item.id, 'upsert', item);
    return item;
  }

  async findThemeSetting(themeKey: string) {
    const item = await this.prisma.themeSetting.findFirst({
      where: {
        storeId: this.storeId(),
        themeKey,
        isPublished: true,
        isActive: true,
      },
    });

    if (!item) {
      throw new NotFoundException('theme settings not found');
    }

    return item;
  }

  async listStorePages(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.storePage.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { title: 'asc' }],
        ...paging,
      }),
      this.prisma.storePage.count({ where }),
    ]);

    return { items, total };
  }

  async createStorePage(body: Record<string, unknown>) {
    const title = this.requiredString(body, 'title');
    const slug = this.slug(body, title);
    await this.assertStorePageSlugAvailable(this.storeId(), slug);
    const page = await this.prisma.storePage.create({
      data: {
        storeId: this.storeId(),
        title,
        slug,
        content: this.optionalJson(body, 'content'),
        isPublished: this.optionalBoolean(body, 'isPublished') ?? false,
      },
    });

    await this.logChange('store', 'pages', page.id, 'create', page);
    return page;
  }

  async updateStorePage(pageId: string, body: Record<string, unknown>) {
    await this.assertStorePage(pageId);
    const data: Prisma.StorePageUpdateInput = {};
    const title = this.optionalString(body, 'title');
    const slug = this.optionalString(body, 'slug');

    if (title !== undefined) data.title = title;
    if (slug !== undefined || title !== undefined) {
      // The guard above guarantees at least one of title/slug is defined.
      const nextSlug = this.slug(body, (title ?? slug) as string);
      await this.assertStorePageSlugAvailable(this.storeId(), nextSlug, pageId);
      data.slug = nextSlug;
    }
    if (body.content !== undefined)
      data.content = this.optionalJson(body, 'content');
    this.assignOptionalBoolean(data, body, 'isPublished');
    this.assignOptionalNumber(data, body, 'sortOrder');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException('at least one page field is required');
    }

    const page = await this.prisma.storePage.update({
      where: { id: pageId },
      data,
    });

    await this.logChange('store', 'pages', page.id, 'update', page);
    return page;
  }

  async deleteStorePage(pageId: string) {
    await this.assertStorePage(pageId);
    const page = await this.prisma.storePage.update({
      where: { id: pageId },
      data: { isActive: false },
    });

    await this.logChange('store', 'pages', page.id, 'delete', page);
    return page;
  }

  async findStorePage(slug: string) {
    const page = await this.prisma.storePage.findFirst({
      where: {
        storeId: this.storeId(),
        slug,
        isPublished: true,
        isActive: true,
      },
    });

    if (!page) {
      throw new NotFoundException('page not found');
    }

    return page;
  }

  async listNavigationMenus(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.navigationMenu.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { title: 'asc' }],
        ...paging,
      }),
      this.prisma.navigationMenu.count({ where }),
    ]);

    return { items, total };
  }

  async upsertNavigationMenu(body: Record<string, unknown>) {
    const handle = this.requiredString(body, 'handle');
    const menu = await this.prisma.navigationMenu.upsert({
      where: { storeId_handle: { storeId: this.storeId(), handle } },
      create: {
        storeId: this.storeId(),
        handle,
        title: this.requiredString(body, 'title'),
        items: this.requiredJson(body, 'items'),
      },
      update: {
        title: this.requiredString(body, 'title'),
        items: this.requiredJson(body, 'items'),
      },
    });

    await this.logChange('store', 'navigation_menus', menu.id, 'upsert', menu);
    return menu;
  }

  async findNavigationMenu(handle: string) {
    const menu = await this.prisma.navigationMenu.findFirst({
      where: { storeId: this.storeId(), handle, isActive: true },
    });

    if (!menu) {
      throw new NotFoundException('navigation menu not found');
    }

    return menu;
  }

  async upsertPaymentProvider(body: Record<string, unknown>) {
    const provider = this.requiredString(body, 'provider');
    const settings = this.requiredJson(body, 'settings');
    const item = await this.prisma.paymentProviderSetting.upsert({
      where: { storeId_provider: { storeId: this.storeId(), provider } },
      create: {
        storeId: this.storeId(),
        provider,
        settings,
        isEnabled: this.optionalBoolean(body, 'isEnabled') ?? false,
      },
      update: {
        settings,
        isEnabled: this.optionalBoolean(body, 'isEnabled') ?? false,
      },
    });

    await this.logChange(
      'store',
      'payment_provider_settings',
      item.id,
      'upsert',
      item
    );
    return item;
  }

  async listPaymentProviders(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.paymentProviderSetting.findMany({
        where,
        orderBy: [{ provider: 'asc' }],
        ...paging,
      }),
      this.prisma.paymentProviderSetting.count({ where }),
    ]);

    return { items, total };
  }

  async updatePaymentProvider(
    paymentProviderId: string,
    body: Record<string, unknown>
  ) {
    await this.assertPaymentProvider(paymentProviderId);
    const data: Prisma.PaymentProviderSettingUpdateInput = {};

    if (body.settings !== undefined)
      data.settings = this.requiredJson(body, 'settings');
    this.assignOptionalBoolean(data, body, 'isEnabled');
    this.assignOptionalNumber(data, body, 'sortOrder');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException(
        'at least one payment provider field is required'
      );
    }

    const item = await this.prisma.paymentProviderSetting.update({
      where: { id: paymentProviderId },
      data,
    });

    await this.logChange(
      'store',
      'payment_provider_settings',
      item.id,
      'update',
      item
    );
    return item;
  }

  async deletePaymentProvider(paymentProviderId: string) {
    await this.assertPaymentProvider(paymentProviderId);
    const item = await this.prisma.paymentProviderSetting.update({
      where: { id: paymentProviderId },
      data: { isActive: false },
    });

    await this.logChange(
      'store',
      'payment_provider_settings',
      item.id,
      'delete',
      item
    );
    return item;
  }

  async listShippingZones(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.shippingZone.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { name: 'asc' }],
        ...paging,
      }),
      this.prisma.shippingZone.count({ where }),
    ]);

    return { items, total };
  }

  async createShippingZone(body: Record<string, unknown>) {
    const zone = await this.prisma.shippingZone.create({
      data: {
        storeId: this.storeId(),
        name: this.requiredString(body, 'name'),
        countries: this.requiredJson(body, 'countries'),
      },
    });

    await this.logChange('store', 'shipping_zones', zone.id, 'create', zone);
    return zone;
  }

  async updateShippingZone(
    shippingZoneId: string,
    body: Record<string, unknown>
  ) {
    await this.assertShippingZone(shippingZoneId);
    const data: Prisma.ShippingZoneUpdateInput = {};

    this.assignOptionalString(data, body, 'name');
    if (body.countries !== undefined)
      data.countries = this.requiredJson(body, 'countries');
    this.assignOptionalNumber(data, body, 'sortOrder');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException(
        'at least one shipping zone field is required'
      );
    }

    const zone = await this.prisma.shippingZone.update({
      where: { id: shippingZoneId },
      data,
    });

    await this.logChange('store', 'shipping_zones', zone.id, 'update', zone);
    return zone;
  }

  async deleteShippingZone(shippingZoneId: string) {
    await this.assertShippingZone(shippingZoneId);
    const zone = await this.prisma.shippingZone.update({
      where: { id: shippingZoneId },
      data: { isActive: false },
    });

    await this.logChange('store', 'shipping_zones', zone.id, 'delete', zone);
    return zone;
  }

  async listShippingRates(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = {
      storeId: this.storeId(),
      isActive: true,
      ...(query.zoneId ? { zoneId: query.zoneId } : {}),
    };
    const [items, total] = await Promise.all([
      this.prisma.shippingRate.findMany({
        where,
        orderBy: [{ sortOrder: 'asc' }, { name: 'asc' }],
        ...paging,
      }),
      this.prisma.shippingRate.count({ where }),
    ]);

    return { items, total };
  }

  async createShippingRate(body: Record<string, unknown>) {
    const zoneId = this.requiredString(body, 'zoneId');
    await this.assertShippingZone(zoneId);
    const rate = await this.prisma.shippingRate.create({
      data: {
        storeId: this.storeId(),
        zoneId,
        name: this.requiredString(body, 'name'),
        amount: this.requiredNumber(body, 'amount'),
        currency: this.optionalString(body, 'currency') ?? 'INR',
      },
    });

    await this.logChange('store', 'shipping_rates', rate.id, 'create', rate);
    return rate;
  }

  async updateShippingRate(
    shippingRateId: string,
    body: Record<string, unknown>
  ) {
    await this.assertShippingRate(shippingRateId);
    const data: Prisma.ShippingRateUpdateInput = {};
    const currency = this.optionalString(body, 'currency');

    this.assignOptionalString(data, body, 'name');
    this.assignOptionalNumber(data, body, 'amount');
    if (currency !== undefined) data.currency = currency.toUpperCase();
    this.assignOptionalNumber(data, body, 'sortOrder');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException(
        'at least one shipping rate field is required'
      );
    }

    const rate = await this.prisma.shippingRate.update({
      where: { id: shippingRateId },
      data,
    });

    await this.logChange('store', 'shipping_rates', rate.id, 'update', rate);
    return rate;
  }

  async deleteShippingRate(shippingRateId: string) {
    await this.assertShippingRate(shippingRateId);
    const rate = await this.prisma.shippingRate.update({
      where: { id: shippingRateId },
      data: { isActive: false },
    });

    await this.logChange('store', 'shipping_rates', rate.id, 'delete', rate);
    return rate;
  }

  async listOrderPayments(orderId: string, query: RawQuery = {}) {
    await this.assertOrder(orderId);
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), orderId, isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.payment.findMany({
        where,
        orderBy: { createdAt: 'desc' },
        ...paging,
      }),
      this.prisma.payment.count({ where }),
    ]);

    return { items, total };
  }

  async listOrderShipments(orderId: string, query: RawQuery = {}) {
    await this.assertOrder(orderId);
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), orderId, isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.shipment.findMany({
        where,
        orderBy: { createdAt: 'desc' },
        ...paging,
      }),
      this.prisma.shipment.count({ where }),
    ]);

    return { items, total };
  }

  async createShipment(orderId: string, body: Record<string, unknown>) {
    await this.assertOrder(orderId);
    const status = this.optionalString(body, 'status') ?? 'pending';
    const shipment = await this.prisma.shipment.create({
      data: {
        storeId: this.storeId(),
        orderId,
        carrier: this.optionalString(body, 'carrier'),
        trackingNumber: this.optionalString(body, 'trackingNumber'),
        status,
        shippedOn: this.optionalDate(body, 'shippedOn'),
        deliveredOn: this.optionalDate(body, 'deliveredOn'),
      },
    });

    await this.logChange('sales', 'shipments', shipment.id, 'create', shipment);
    return shipment;
  }

  async updateShipment(shipmentId: string, body: Record<string, unknown>) {
    await this.assertShipment(shipmentId);
    const data: Prisma.ShipmentUpdateInput = {};

    this.assignOptionalString(data, body, 'carrier');
    this.assignOptionalString(data, body, 'trackingNumber');
    this.assignOptionalString(data, body, 'status');
    this.assignOptionalNumber(data, body, 'sortOrder');
    if (body.shippedOn !== undefined)
      data.shippedOn = this.optionalDate(body, 'shippedOn');
    if (body.deliveredOn !== undefined)
      data.deliveredOn = this.optionalDate(body, 'deliveredOn');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException('at least one shipment field is required');
    }

    const shipment = await this.prisma.shipment.update({
      where: { id: shipmentId },
      data,
    });

    await this.logChange('sales', 'shipments', shipment.id, 'update', shipment);
    return shipment;
  }

  async listOrderRefunds(orderId: string, query: RawQuery = {}) {
    await this.assertOrder(orderId);
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), orderId, isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.refund.findMany({
        where,
        orderBy: { createdAt: 'desc' },
        ...paging,
      }),
      this.prisma.refund.count({ where }),
    ]);

    return { items, total };
  }

  async createRefund(orderId: string, body: Record<string, unknown>) {
    await this.assertOrder(orderId);
    const paymentId = this.optionalString(body, 'paymentId');

    if (paymentId) {
      await this.assertPayment(orderId, paymentId);
    }

    const amount = this.requiredNumber(body, 'amount');
    if (amount <= 0) {
      throw new BadRequestException('amount must be greater than 0');
    }

    const refundItems = this.refundItems(body);
    await this.assertRefundItems(orderId, refundItems);
    const refund = await this.prisma.$transaction(async (tx) => {
      // First statement in the transaction, deliberately: audit.fn_log_change()
      // reads this setting at the moment it fires, so anything written before
      // this point is attributed to `system`.
      await this.auditTx?.publishActor(tx);
      const created = await tx.refund.create({
        data: {
          storeId: this.storeId(),
          orderId,
          paymentId,
          status: this.optionalString(body, 'status') ?? 'pending',
          amount,
          reason: this.optionalString(body, 'reason'),
        },
      });

      await Promise.all(
        refundItems.map((item) =>
          tx.refundItem.create({
            data: {
              storeId: this.storeId(),
              refundId: created.id,
              orderItemId: item.orderItemId,
              quantity: item.quantity,
              amount: item.amount,
            },
          })
        )
      );

      if (paymentId) {
        await tx.paymentTransaction.create({
          data: {
            storeId: this.storeId(),
            paymentId,
            type: 'refund',
            status: created.status,
            amount,
            rawPayload: {
              refundId: created.id,
              reason: created.reason,
              items: refundItems,
            },
          },
        });
      }

      return created;
    });

    await this.logChange('sales', 'refunds', refund.id, 'create', {
      ...refund,
      items: refundItems,
    });

    await this.sendRefundEmail(orderId, refund.amount);

    return refund;
  }

  private async sendRefundEmail(orderId: string, amountInPaise: number): Promise<void> {
    const order = await this.prisma.order.findFirst({
      where: { id: orderId },
      select: { orderNumber: true, email: true, customerId: true },
    });

    if (!order) {
      return;
    }

    const customer = order.customerId
      ? await this.prisma.user.findFirst({ where: { id: order.customerId }, select: { name: true, email: true } })
      : null;
    const to = customer?.email ?? order.email;

    if (!to) {
      return;
    }

    await this.emailService?.sendTemplated('order_refunded', to, {
      customerName: customer?.name ?? 'there',
      orderNumber: order.orderNumber,
      refundAmount: `₹${(amountInPaise / 100).toFixed(2)}`,
    });
  }

  async updateOrderPaymentStatus(
    orderId: string,
    body: Record<string, unknown>
  ) {
    await this.assertOrder(orderId);
    const paymentStatus = this.requiredString(body, 'paymentStatus');
    const order = await this.prisma.order.update({
      where: { id: orderId },
      data: { paymentStatus },
    });

    await this.logChange('sales', 'orders', order.id, 'update', order);
    return order;
  }

  async updateOrderFulfillmentStatus(
    orderId: string,
    body: Record<string, unknown>
  ) {
    await this.assertOrder(orderId);
    const fulfillmentStatus = this.requiredString(body, 'fulfillmentStatus');
    const order = await this.prisma.order.update({
      where: { id: orderId },
      data: { fulfillmentStatus },
    });

    await this.logChange('sales', 'orders', order.id, 'update', order);
    return order;
  }

  async listDiscountCodes(query: RawQuery = {}) {
    const paging = this.paging(query);
    // No isActive filter here (unlike most list*() methods) — discount codes
    // have no separate hard-delete/purge endpoint, so isActive doubles as both
    // the merchant-facing "Active" toggle and the soft-delete marker. Filtering
    // it out would make a deactivated code (or one the user turns off via the
    // Active toggle) permanently disappear from the admin list with no way
    // back — see updateDiscountCode's isActive handling.
    const where = { storeId: this.storeId() };
    const [items, total] = await Promise.all([
      this.prisma.discountCode.findMany({
        where,
        orderBy: [{ createdAt: 'desc' }],
        ...paging,
      }),
      this.prisma.discountCode.count({ where }),
    ]);

    return { items, total };
  }

  private validateDiscountCodeEligibility(
    body: Record<string, unknown>
  ): 'all' | 'new_customers' | undefined {
    const value = this.optionalString(body, 'customerEligibility');
    if (value === undefined) return undefined;
    if (value !== 'all' && value !== 'new_customers') {
      throw new BadRequestException(
        "customerEligibility must be 'all' or 'new_customers'"
      );
    }
    return value;
  }

  async checkDiscountCode(code: string): Promise<{
    available: boolean;
    reason?: 'taken' | 'invalid-format';
  }> {
    const v = code.trim().toUpperCase();
    if (!/^[A-Z0-9_-]{3,32}$/.test(v)) {
      return { available: false, reason: 'invalid-format' };
    }

    const existing = await this.prisma.discountCode.findFirst({
      where: { storeId: this.storeId(), code: v, isActive: true },
      select: { id: true },
    });
    return existing ? { available: false, reason: 'taken' } : { available: true };
  }

  async createDiscountCode(body: Record<string, unknown>) {
    const minOrderAmount = this.optionalNumber(body, 'minOrderAmount');
    const maxDiscountAmount = this.optionalNumber(body, 'maxDiscountAmount');
    if (minOrderAmount !== undefined && minOrderAmount < 0) {
      throw new BadRequestException('minOrderAmount must be greater than or equal to 0');
    }
    if (maxDiscountAmount !== undefined && maxDiscountAmount < 0) {
      throw new BadRequestException('maxDiscountAmount must be greater than or equal to 0');
    }
    const customerEligibility = this.validateDiscountCodeEligibility(body) ?? 'all';

    const item = await this.prisma.discountCode.create({
      data: {
        storeId: this.storeId(),
        code: this.requiredString(body, 'code').toUpperCase(),
        type: this.requiredString(body, 'type'),
        value: this.requiredNumber(body, 'value'),
        minOrderAmount,
        maxDiscountAmount,
        customerEligibility,
        stackable: this.optionalBoolean(body, 'stackable') ?? false,
        isActive: this.optionalBoolean(body, 'isActive') ?? true,
        usageLimit: this.optionalNumber(body, 'usageLimit'),
        startsOn: this.optionalDate(body, 'startsOn'),
        endsOn: this.optionalDate(body, 'endsOn'),
      },
    });

    await this.logChange(
      'marketing',
      'discount_codes',
      item.id,
      'create',
      item
    );
    return item;
  }

  async updateDiscountCode(
    discountCodeId: string,
    body: Record<string, unknown>
  ) {
    await this.assertDiscountCode(discountCodeId);
    const data: Prisma.DiscountCodeUpdateInput = {};
    const code = this.optionalString(body, 'code');

    if (code !== undefined) data.code = code.toUpperCase();
    this.assignOptionalString(data, body, 'type');
    this.assignOptionalNumber(data, body, 'value');
    this.assignOptionalNumber(data, body, 'minOrderAmount');
    this.assignOptionalNumber(data, body, 'maxDiscountAmount');
    this.assignOptionalBoolean(data, body, 'stackable');
    this.assignOptionalBoolean(data, body, 'isActive');
    this.assignOptionalNumber(data, body, 'usageLimit');
    this.assignOptionalNumber(data, body, 'sortOrder');
    const eligibility = this.validateDiscountCodeEligibility(body);
    if (eligibility !== undefined) data.customerEligibility = eligibility;
    if (body.startsOn !== undefined)
      data.startsOn = this.optionalDate(body, 'startsOn');
    if (body.endsOn !== undefined)
      data.endsOn = this.optionalDate(body, 'endsOn');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException(
        'at least one discount code field is required'
      );
    }

    const item = await this.prisma.discountCode.update({
      where: { id: discountCodeId },
      data,
    });

    await this.logChange(
      'marketing',
      'discount_codes',
      item.id,
      'update',
      item
    );
    return item;
  }

  async deleteDiscountCode(discountCodeId: string) {
    await this.assertDiscountCode(discountCodeId);
    const item = await this.prisma.discountCode.update({
      where: { id: discountCodeId },
      data: { isActive: false },
    });

    await this.logChange(
      'marketing',
      'discount_codes',
      item.id,
      'delete',
      item
    );
    return item;
  }

  async previewDiscountCode(body: Record<string, unknown>) {
    const subtotal = this.requiredNumber(body, 'subtotal');
    const code = this.requiredString(body, 'code').toUpperCase();

    if (subtotal < 0) {
      throw new BadRequestException(
        'subtotal must be greater than or equal to 0'
      );
    }

    const now = new Date();
    const discount = await this.prisma.discountCode.findFirst({
      where: {
        storeId: this.storeId(),
        code,
        isActive: true,
        OR: [{ startsOn: null }, { startsOn: { lte: now } }],
        AND: [{ OR: [{ endsOn: null }, { endsOn: { gte: now } }] }],
      },
    });

    if (!discount) {
      throw new NotFoundException('discount code not found');
    }

    if (discount.usageLimit != null && discount.usedCount >= discount.usageLimit) {
      throw new BadRequestException('discount code usage limit reached');
    }

    if (discount.minOrderAmount != null && subtotal < discount.minOrderAmount) {
      throw new BadRequestException(
        `order subtotal must be at least ${discount.minOrderAmount} to use this code`
      );
    }

    let discountTotal =
      discount.type === 'percentage'
        ? Math.min(Math.round((subtotal * discount.value) / 10000), subtotal)
        : discount.type === 'fixed'
        ? Math.min(discount.value, subtotal)
        : 0; // free_shipping — no line-item discount, shipping is waived at checkout

    if (discount.maxDiscountAmount != null) {
      discountTotal = Math.min(discountTotal, discount.maxDiscountAmount);
    }

    return {
      code: discount.code,
      type: discount.type,
      subtotal,
      discountTotal,
      totalAfterDiscount: subtotal - discountTotal,
      freeShipping: discount.type === 'free_shipping',
    };
  }

  async listPromotions(query: RawQuery = {}) {
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.promotion.findMany({
        where,
        orderBy: [{ createdAt: 'desc' }],
        ...paging,
      }),
      this.prisma.promotion.count({ where }),
    ]);

    return { items, total };
  }

  async createPromotion(body: Record<string, unknown>) {
    const item = await this.prisma.promotion.create({
      data: {
        storeId: this.storeId(),
        name: this.requiredString(body, 'name'),
        status: this.optionalString(body, 'status') ?? 'draft',
        startsOn: this.optionalDate(body, 'startsOn'),
        endsOn: this.optionalDate(body, 'endsOn'),
      },
    });

    await this.logChange('marketing', 'promotions', item.id, 'create', item);
    return item;
  }

  async updatePromotion(promotionId: string, body: Record<string, unknown>) {
    await this.assertPromotion(promotionId);
    const data: Prisma.PromotionUpdateInput = {};

    this.assignOptionalString(data, body, 'name');
    this.assignOptionalString(data, body, 'status');
    this.assignOptionalNumber(data, body, 'sortOrder');
    if (body.startsOn !== undefined)
      data.startsOn = this.optionalDate(body, 'startsOn');
    if (body.endsOn !== undefined)
      data.endsOn = this.optionalDate(body, 'endsOn');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException('at least one promotion field is required');
    }

    const item = await this.prisma.promotion.update({
      where: { id: promotionId },
      data,
    });

    await this.logChange('marketing', 'promotions', item.id, 'update', item);
    return item;
  }

  async deletePromotion(promotionId: string) {
    await this.assertPromotion(promotionId);
    const item = await this.prisma.promotion.update({
      where: { id: promotionId },
      data: { isActive: false },
    });

    await this.logChange('marketing', 'promotions', item.id, 'delete', item);
    return item;
  }

  async createPromotionRule(
    promotionId: string,
    body: Record<string, unknown>
  ) {
    await this.assertPromotion(promotionId);
    const item = await this.prisma.promotionRule.create({
      data: {
        storeId: this.storeId(),
        promotionId,
        ruleType: this.requiredString(body, 'ruleType'),
        conditions: this.requiredJson(body, 'conditions'),
        actions: this.requiredJson(body, 'actions'),
      },
    });

    await this.logChange(
      'marketing',
      'promotion_rules',
      item.id,
      'create',
      item
    );
    return item;
  }

  async listPromotionRules(promotionId: string, query: RawQuery = {}) {
    await this.assertPromotion(promotionId);
    const paging = this.paging(query);
    const where = { storeId: this.storeId(), promotionId, isActive: true };
    const [items, total] = await Promise.all([
      this.prisma.promotionRule.findMany({
        where,
        orderBy: [{ createdAt: 'desc' }],
        ...paging,
      }),
      this.prisma.promotionRule.count({ where }),
    ]);

    return { items, total };
  }

  async updatePromotionRule(
    promotionRuleId: string,
    body: Record<string, unknown>
  ) {
    await this.assertPromotionRule(promotionRuleId);
    const data: Prisma.PromotionRuleUpdateInput = {};

    this.assignOptionalString(data, body, 'ruleType');
    if (body.conditions !== undefined)
      data.conditions = this.requiredJson(body, 'conditions');
    if (body.actions !== undefined)
      data.actions = this.requiredJson(body, 'actions');
    this.assignOptionalNumber(data, body, 'sortOrder');

    if (Object.keys(data).length === 0) {
      throw new BadRequestException(
        'at least one promotion rule field is required'
      );
    }

    const item = await this.prisma.promotionRule.update({
      where: { id: promotionRuleId },
      data,
    });

    await this.logChange(
      'marketing',
      'promotion_rules',
      item.id,
      'update',
      item
    );
    return item;
  }

  async deletePromotionRule(promotionRuleId: string) {
    await this.assertPromotionRule(promotionRuleId);
    const item = await this.prisma.promotionRule.update({
      where: { id: promotionRuleId },
      data: { isActive: false },
    });

    await this.logChange(
      'marketing',
      'promotion_rules',
      item.id,
      'delete',
      item
    );
    return item;
  }

  async listChangeLogs(query: RawQuery) {
    const paging = this.paging(query);
    const where = {
      storeId: this.storeId(),
      ...(query.schemaName ? { schemaName: query.schemaName } : {}),
      ...(query.tableName ? { tableName: query.tableName } : {}),
      ...(query.recordId ? { recordId: query.recordId } : {}),
      ...(query.operation ? { operation: query.operation } : {}),
      ...(query.changedBy ? { changedBy: query.changedBy } : {}),
    };
    const [items, total] = await Promise.all([
      this.prisma.changeLog.findMany({
        where,
        orderBy: { changedOn: 'desc' },
        ...paging,
      }),
      this.prisma.changeLog.count({ where }),
    ]);

    return { items, total };
  }

  async listInterfaceLogs(query: RawQuery) {
    const paging = this.paging(query);
    const where = {
      storeId: this.storeId(),
      ...(query.method ? { method: query.method.toUpperCase() } : {}),
      ...(query.path ? { path: { contains: query.path } } : {}),
      ...(query.statusCode ? { statusCode: Number(query.statusCode) } : {}),
      ...(query.correlationId ? { correlationId: query.correlationId } : {}),
      ...(query.userId ? { userId: query.userId } : {}),
    };
    const [items, total] = await Promise.all([
      this.prisma.interfaceLog.findMany({
        where,
        orderBy: { requestedOn: 'desc' },
        ...paging,
      }),
      this.prisma.interfaceLog.count({ where }),
    ]);

    return { items, total };
  }

  private async assertProduct(productId: string): Promise<void> {
    const product = await this.prisma.product.findFirst({
      where: { id: productId, storeId: this.storeId(), active: true },
      select: { id: true },
    });

    if (!product) {
      throw new NotFoundException('product not found');
    }
  }

  private async assertOrganization(organizationId: string): Promise<void> {
    const organization = await this.prisma.organization.findFirst({
      where: { id: organizationId, isActive: true },
      select: { id: true },
    });

    if (!organization) {
      throw new NotFoundException('organization not found');
    }
  }

  private async assertStore(storeId: string): Promise<void> {
    const store = await this.prisma.store.findFirst({
      where: {
        id: storeId,
        organizationId: this.organizationId(),
        isActive: true,
      },
      select: { id: true },
    });

    if (!store) {
      throw new NotFoundException('store not found');
    }
  }

  private async assertUser(userId: string): Promise<void> {
    const user = await this.prisma.user.findFirst({
      where: { id: userId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!user) {
      throw new NotFoundException('user not found');
    }
  }

  private async assertCustomer(customerId: string): Promise<void> {
    const customer = await this.prisma.customer.findFirst({
      where: { id: customerId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!customer) {
      throw new NotFoundException('customer not found');
    }
  }

  private async assertCustomerAddress(addressId: string): Promise<void> {
    const address = await this.prisma.customerAddress.findFirst({
      where: { id: addressId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!address) {
      throw new NotFoundException('customer address not found');
    }
  }

  private async assertCustomerTag(tagId: string): Promise<void> {
    const tag = await this.prisma.customerTag.findFirst({
      where: { id: tagId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!tag) {
      throw new NotFoundException('customer tag not found');
    }
  }

  private async assertCustomerSession(sessionId: string): Promise<void> {
    const session = await this.prisma.customerSession.findFirst({
      where: { id: sessionId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!session) {
      throw new NotFoundException('customer session not found');
    }
  }

  private async assertCustomerWishlistItem(
    wishlistItemId: string
  ): Promise<void> {
    const item = await this.prisma.customerWishlistItem.findFirst({
      where: { id: wishlistItemId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!item) {
      throw new NotFoundException('customer wishlist item not found');
    }
  }

  private async assertStoreStaff(staffId: string): Promise<void> {
    const staff = await this.prisma.storeStaff.findFirst({
      where: { id: staffId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!staff) {
      throw new NotFoundException('store staff not found');
    }
  }

  private async assertRole(roleId: string): Promise<void> {
    const role = await this.prisma.platformRole.findFirst({
      where: { id: roleId, isActive: true },
      select: { id: true },
    });

    if (!role) {
      throw new NotFoundException('role not found');
    }
  }

  private async assertPermission(permissionId: string): Promise<void> {
    const permission = await this.prisma.platformPermission.findFirst({
      where: { id: permissionId, isActive: true },
      select: { id: true },
    });

    if (!permission) {
      throw new NotFoundException('permission not found');
    }
  }

  private async assertRolePermission(rolePermissionId: string): Promise<void> {
    const rolePermission = await this.prisma.platformRolePermission.findFirst({
      where: { id: rolePermissionId, isActive: true },
      select: { id: true },
    });

    if (!rolePermission) {
      throw new NotFoundException('role permission not found');
    }
  }

  private async assertCountry(countryId: string): Promise<void> {
    const country = await this.prisma.country.findFirst({
      where: { id: countryId, isActive: true },
      select: { id: true },
    });

    if (!country) {
      throw new NotFoundException('country not found');
    }
  }

  private async assertState(stateId: string): Promise<void> {
    const state = await this.prisma.state.findFirst({
      where: { id: stateId, isActive: true },
      select: { id: true },
    });

    if (!state) {
      throw new NotFoundException('state not found');
    }
  }

  private async assertTaxSlabProfile(taxProfileId: string) {
    const profile = await this.prisma.taxSlabProfile.findFirst({
      where: { id: taxProfileId, storeId: this.storeId(), isActive: true },
    });

    if (!profile) {
      throw new NotFoundException('tax slab profile not found');
    }
    return profile;
  }

  private async assertCollection(collectionId: string): Promise<void> {
    const collection = await this.prisma.collection.findFirst({
      where: { id: collectionId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!collection) {
      throw new NotFoundException('collection not found');
    }
  }

  private async assertProductOption(optionId: string): Promise<void> {
    const option = await this.prisma.productOption.findFirst({
      where: { id: optionId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!option) {
      throw new NotFoundException('product option not found');
    }
  }

  private async assertBrand(brandId: string): Promise<void> {
    const brand = await this.prisma.brand.findFirst({
      where: { id: brandId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!brand) {
      throw new NotFoundException('brand not found');
    }
  }

  private async assertProductVariant(
    productId: string,
    variantId: string
  ): Promise<void> {
    const variant = await this.prisma.productVariant.findFirst({
      where: {
        id: variantId,
        productId,
        storeId: this.storeId(),
        isActive: true,
      },
      select: { id: true },
    });

    if (!variant) {
      throw new NotFoundException('product variant not found');
    }
  }

  private async assertProductImage(imageId: string): Promise<void> {
    const image = await this.prisma.productImage.findFirst({
      where: { id: imageId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!image) {
      throw new NotFoundException('product image not found');
    }
  }

  private async assertInventoryLocation(locationId: string): Promise<void> {
    const location = await this.prisma.inventoryLocation.findFirst({
      where: { id: locationId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!location) {
      throw new NotFoundException('inventory location not found');
    }
  }

  private async assertInventoryItem(inventoryItemId: string): Promise<void> {
    const item = await this.prisma.inventoryItem.findFirst({
      where: { id: inventoryItemId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!item) {
      throw new NotFoundException('inventory item not found');
    }
  }

  private async assertInventoryLevel(inventoryLevelId: string): Promise<void> {
    const level = await this.prisma.inventoryLevel.findFirst({
      where: { id: inventoryLevelId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!level) {
      throw new NotFoundException('inventory level not found');
    }
  }

  private async findInventoryLevelForReservation(
    inventoryLevelId: string
  ): Promise<{
    id: string;
    inventoryItemId: string;
    locationId: string;
    available: number;
    committed: number;
  }> {
    const level = await this.prisma.inventoryLevel.findFirst({
      where: { id: inventoryLevelId, storeId: this.storeId(), isActive: true },
      select: {
        id: true,
        inventoryItemId: true,
        locationId: true,
        available: true,
        committed: true,
      },
    });

    if (!level) {
      throw new NotFoundException('inventory level not found');
    }

    return level;
  }

  private async assertStorePage(pageId: string): Promise<void> {
    const page = await this.prisma.storePage.findFirst({
      where: { id: pageId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!page) {
      throw new NotFoundException('page not found');
    }
  }

  private async assertStoreDomain(domainId: string): Promise<void> {
    const domain = await this.prisma.storeDomain.findFirst({
      where: { id: domainId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!domain) {
      throw new NotFoundException('store domain not found');
    }
  }

  private async assertPaymentProvider(
    paymentProviderId: string
  ): Promise<void> {
    const provider = await this.prisma.paymentProviderSetting.findFirst({
      where: { id: paymentProviderId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!provider) {
      throw new NotFoundException('payment provider not found');
    }
  }

  private async assertShippingZone(shippingZoneId: string): Promise<void> {
    const zone = await this.prisma.shippingZone.findFirst({
      where: { id: shippingZoneId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!zone) {
      throw new NotFoundException('shipping zone not found');
    }
  }

  private async assertShippingRate(shippingRateId: string): Promise<void> {
    const rate = await this.prisma.shippingRate.findFirst({
      where: { id: shippingRateId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!rate) {
      throw new NotFoundException('shipping rate not found');
    }
  }

  private async assertDiscountCode(discountCodeId: string): Promise<void> {
    // No isActive filter — see listDiscountCodes for why; a deactivated code
    // must still be updatable (including re-enabling it via the Active toggle).
    const discountCode = await this.prisma.discountCode.findFirst({
      where: { id: discountCodeId, storeId: this.storeId() },
      select: { id: true },
    });

    if (!discountCode) {
      throw new NotFoundException('discount code not found');
    }
  }

  private async assertPromotion(promotionId: string): Promise<void> {
    const promotion = await this.prisma.promotion.findFirst({
      where: { id: promotionId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!promotion) {
      throw new NotFoundException('promotion not found');
    }
  }

  private async assertPromotionRule(promotionRuleId: string): Promise<void> {
    const promotionRule = await this.prisma.promotionRule.findFirst({
      where: { id: promotionRuleId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!promotionRule) {
      throw new NotFoundException('promotion rule not found');
    }
  }

  private async assertOrder(orderId: string): Promise<void> {
    const order = await this.prisma.order.findFirst({
      where: { id: orderId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!order) {
      throw new NotFoundException('order not found');
    }
  }

  private async assertPayment(
    orderId: string,
    paymentId: string
  ): Promise<void> {
    const payment = await this.prisma.payment.findFirst({
      where: {
        id: paymentId,
        orderId,
        storeId: this.storeId(),
        isActive: true,
      },
      select: { id: true },
    });

    if (!payment) {
      throw new NotFoundException('payment not found');
    }
  }

  private async assertShipment(shipmentId: string): Promise<void> {
    const shipment = await this.prisma.shipment.findFirst({
      where: { id: shipmentId, storeId: this.storeId(), isActive: true },
      select: { id: true },
    });

    if (!shipment) {
      throw new NotFoundException('shipment not found');
    }
  }

  private async assertRefundItems(
    orderId: string,
    items: Array<{ orderItemId: string; quantity: number; amount: number }>
  ): Promise<void> {
    if (!items.length) {
      return;
    }

    const existing = await this.prisma.orderItem.findMany({
      where: {
        storeId: this.storeId(),
        orderId,
        id: { in: items.map((item) => item.orderItemId) },
        isActive: true,
      },
      select: { id: true },
    });

    if (existing.length !== items.length) {
      throw new NotFoundException('refund item not found');
    }
  }

  private sanitizeFilename(name: string): string {
    return name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-100);
  }

  private requiredString(body: Record<string, unknown>, key: string): string {
    const value = body[key];

    if (typeof value !== 'string' || value.trim() === '') {
      throw new BadRequestException(`${key} is required`);
    }

    return value.trim();
  }

  private optionalString(
    body: Record<string, unknown>,
    key: string
  ): string | undefined {
    const value = body[key];
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }

  private requiredNumber(body: Record<string, unknown>, key: string): number {
    const value = body[key];
    const numberValue = typeof value === 'number' ? value : Number(value);

    if (!Number.isFinite(numberValue)) {
      throw new BadRequestException(`${key} must be a number`);
    }

    return numberValue;
  }

  private requiredPositiveInteger(
    body: Record<string, unknown>,
    key: string
  ): number {
    const value = this.requiredNumber(body, key);

    if (!Number.isInteger(value) || value < 1) {
      throw new BadRequestException(`${key} must be at least 1`);
    }

    return value;
  }

  private optionalNumber(
    body: Record<string, unknown>,
    key: string
  ): number | undefined {
    if (body[key] === undefined || body[key] === null || body[key] === '') {
      return undefined;
    }

    return this.requiredNumber(body, key);
  }

  private optionalBoolean(
    body: Record<string, unknown>,
    key: string
  ): boolean | undefined {
    const value = body[key];

    if (value === undefined || value === null || value === '') {
      return undefined;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (value === 'true') {
      return true;
    }

    if (value === 'false') {
      return false;
    }

    throw new BadRequestException(`${key} must be boolean`);
  }

  private requiredJson(
    body: Record<string, unknown>,
    key: string
  ): Prisma.InputJsonValue {
    if (body[key] === undefined) {
      throw new BadRequestException(`${key} is required`);
    }

    return body[key] as Prisma.InputJsonValue;
  }

  private optionalJson(
    body: Record<string, unknown>,
    key: string
  ): Prisma.InputJsonValue | undefined {
    return body[key] === undefined
      ? undefined
      : (body[key] as Prisma.InputJsonValue);
  }

  private optionalDate(
    body: Record<string, unknown>,
    key: string
  ): Date | undefined {
    const value = body[key];

    if (typeof value !== 'string' || value.trim() === '') {
      return undefined;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      throw new BadRequestException(`${key} must be a valid date`);
    }

    return date;
  }

  private requiredDate(body: Record<string, unknown>, key: string): Date {
    const date = this.optionalDate(body, key);

    if (!date) {
      throw new BadRequestException(`${key} must be a valid date`);
    }

    return date;
  }

  private refundItems(
    body: Record<string, unknown>
  ): Array<{ orderItemId: string; quantity: number; amount: number }> {
    const value = body.items;

    if (value === undefined) {
      return [];
    }

    if (!Array.isArray(value)) {
      throw new BadRequestException('items must be an array');
    }

    return value.map((item, index) => {
      if (typeof item !== 'object' || item === null || Array.isArray(item)) {
        throw new BadRequestException(`items.${index} must be an object`);
      }

      const record = item as Record<string, unknown>;
      const orderItemId = this.requiredString(record, 'orderItemId');
      const quantity = this.requiredNumber(record, 'quantity');
      const amount = this.requiredNumber(record, 'amount');

      if (!Number.isInteger(quantity) || quantity < 1) {
        throw new BadRequestException(
          `items.${index}.quantity must be at least 1`
        );
      }

      if (amount <= 0) {
        throw new BadRequestException(
          `items.${index}.amount must be greater than 0`
        );
      }

      return { orderItemId, quantity, amount };
    });
  }

  private assignOptionalString(
    data: object,
    body: Record<string, unknown>,
    key: string
  ): void {
    const value = this.optionalString(body, key);
    if (value !== undefined) {
      (data as Record<string, unknown>)[key] = value;
    }
  }

  private assignOptionalNumber(
    data: object,
    body: Record<string, unknown>,
    key: string
  ): void {
    const value = this.optionalNumber(body, key);
    if (value !== undefined) {
      (data as Record<string, unknown>)[key] = value;
    }
  }

  /*
   * The `assignOptional*` helpers above treat an explicit null as "not
   * provided", which is right for a PATCH that only names the fields it
   * changes. It is wrong for a form that submits its whole shape every time:
   * clearing a field there sends null, and the record would silently keep its
   * old value.
   *
   * The `assignNullable*` helpers below make that distinction — absent still
   * means "leave alone", but null means "clear it". Only for columns that are
   * actually nullable.
   */

  private assignNullableString(
    data: object,
    body: Record<string, unknown>,
    key: string
  ): void {
    if (body[key] === undefined) return;
    const value = body[key];
    (data as Record<string, unknown>)[key] =
      typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  private assignNullableNumber(
    data: object,
    body: Record<string, unknown>,
    key: string
  ): void {
    if (body[key] === undefined) return;
    (data as Record<string, unknown>)[key] = this.optionalNumber(body, key) ?? null;
  }

  private assignNullableDate(
    data: object,
    body: Record<string, unknown>,
    key: string
  ): void {
    if (body[key] === undefined) return;
    (data as Record<string, unknown>)[key] = this.optionalDate(body, key) ?? null;
  }

  private assignOptionalBoolean(
    data: object,
    body: Record<string, unknown>,
    key: string
  ): void {
    const value = this.optionalBoolean(body, key);
    if (value !== undefined) {
      (data as Record<string, unknown>)[key] = value;
    }
  }

  private slug(body: Record<string, unknown>, fallback: string): string {
    return (this.optionalString(body, 'slug') ?? fallback)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  // ── Slug uniqueness pre-checks ────────────────────────────────────────────
  // These turn a raw Prisma unique-constraint violation (P2002) into a
  // friendly 409, same pattern as ThemesService.create(). Organization is
  // the only one of these checked globally — it's the top-level tenant, so
  // there's nothing to scope it under. Store/Brand/Collection/StorePage are
  // all scoped to their tenant (org/store) — two different merchants (or
  // two different stores) must each be able to reuse the same slug.

  private async assertOrganizationSlugAvailable(slug: string, excludeId?: string): Promise<void> {
    const existing = await this.prisma.organization.findFirst({
      where: { slug, ...(excludeId ? { id: { not: excludeId } } : {}) },
      select: { id: true },
    });
    if (existing) throw new ConflictException(`Organization slug "${slug}" already exists`);
  }

  private async assertStoreSlugAvailable(organizationId: string, slug: string, excludeId?: string): Promise<void> {
    const existing = await this.prisma.store.findFirst({
      where: { organizationId, slug, ...(excludeId ? { id: { not: excludeId } } : {}) },
      select: { id: true },
    });
    if (existing) throw new ConflictException(`Store slug "${slug}" already exists for this organization`);
  }

  private async assertBrandSlugAvailable(storeId: string, slug: string, excludeId?: string): Promise<void> {
    const existing = await this.prisma.brand.findFirst({
      where: { storeId, slug, ...(excludeId ? { id: { not: excludeId } } : {}) },
      select: { id: true },
    });
    if (existing) throw new ConflictException(`Brand slug "${slug}" already exists`);
  }

  private async assertCollectionSlugAvailable(storeId: string, slug: string, excludeId?: string): Promise<void> {
    const existing = await this.prisma.collection.findFirst({
      where: { storeId, slug, ...(excludeId ? { id: { not: excludeId } } : {}) },
      select: { id: true },
    });
    if (existing) throw new ConflictException(`Collection slug "${slug}" already exists`);
  }

  private async assertStorePageSlugAvailable(storeId: string, slug: string, excludeId?: string): Promise<void> {
    const existing = await this.prisma.storePage.findFirst({
      where: { storeId, slug, ...(excludeId ? { id: { not: excludeId } } : {}) },
      select: { id: true },
    });
    if (existing) throw new ConflictException(`Page slug "${slug}" already exists`);
  }

  private take(query: RawQuery): number {
    const value = Number(query.take ?? 50);
    return Number.isFinite(value) ? Math.min(Math.max(value, 1), 100) : 50;
  }

  private paging(query: RawQuery): { take: number; skip: number } {
    const skipValue = Number(query.skip ?? 0);

    return {
      take: this.take(query),
      skip: Number.isFinite(skipValue) ? Math.max(skipValue, 0) : 0,
    };
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }

  private organizationId(): string {
    return this.storeContext?.organizationId() ?? 'org_demo';
  }

  private async logChange(
    schemaName: string,
    tableName: string,
    recordId: string,
    operation: string,
    newValue: unknown
  ): Promise<void> {
    await this.auditService.logChange({
      schemaName,
      tableName,
      recordId,
      operation,
      newValue,
      source: 'admin-api',
    });
  }
}
